/**
 * CorporateSessions.jsx  — Full Corporate Session lifecycle manager
 *
 * What's new vs the previous version:
 *   • Guest enrollment now includes a "Pick from Company Guests" picker
 *     that shows all PMS guests linked to the company (corporateAccountId).
 *   • Bulk guest enrollment (multi-select + enrol all at once).
 *   • "Create Reservations" per-guest and "Create All" bulk button.
 *   • Reservation status shown per guest (confirmation number, room, etc.).
 *   • "Confirm All Reservations" button once payment threshold is met.
 *   • Final Corporate Invoice print builder (post-checkout, taxes applied).
 *   • Tax rates are dynamic — pulled from property.vatRate / property.tourismTaxRate,
 *     never hardcoded. Proforma prints "VAT Inclusive" with the actual %.
 *   • Session filter is now scoped by account ID (backend fix applied).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import {
  fetchCorporateSessions, createCorporateSession, updateCorporateSession,
  fetchCorporateSessionDetail,
  saveSessionTariff, deleteSessionTariff,
  saveConferenceDay, bulkConferenceDays, deleteConferenceDay,
  addSessionGuest, updateSessionGuest, removeSessionGuest,
  createGuest,
  bulkEnrolSessionGuests,
  savePlannedService, deletePlannedService,
  createSessionPaymentRequest, updateSessionPaymentRequest,
  fetchInstallments, recordInstallment,
  fetchIndividualProformaData, fetchCorporateProformaData,
  createSessionReservations, confirmSessionReservations,
  fetchCorporateSessionInvoiceData,
  fetchCorporateAccountGuests,
  fetchRoomTypes,
  fetchAvailableRoomsForSession,
  checkIn, checkOut,
} from '../../services/api.js';
import { openPrintWindow, resolveAssetUrl } from '../../utils/printWindow.js';
import { fmtNum, amountInWords, renderTemplate, DEFAULT_PAYMENT_REQUEST_BODY, splitAccommodationTax } from '../../utils/invoiceUtils.js';

/* ─────────────────────────────────────────────────────────────────────────── */
/* Constants                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */
const SERVICE_TYPES = [
  // ── Accommodation ─────────────────────────────────────────────────────
  { id:'accommodation',         label:'Accommodation',             icon:'🛏️', unit:'per night',   group:'Accommodation'  },
  // ── Conference (keep original Conference Hall + add Full/Half Day) ───
  { id:'conference',            label:'Conference Hall',           icon:'🏢', unit:'per day',     group:'Conference'     },
  { id:'conference_full_day',   label:'Full Day Conference',       icon:'🏢', unit:'per day',     group:'Conference'     },
  { id:'conference_half_day',   label:'Half Day Conference',       icon:'🏢', unit:'per half day',group:'Conference'     },
  { id:'conference_evening',    label:'Evening Conference',        icon:'🌙', unit:'per session', group:'Conference'     },
  { id:'conference_board_room', label:'Board Room',                icon:'🪑', unit:'per day',     group:'Conference'     },
  // ── Food & Beverage ───────────────────────────────────────────────────
  { id:'restaurant',            label:'Restaurant/Meals',          icon:'🍽️', unit:'per meal',    group:'Food & Beverage'},
  { id:'lunch',                 label:'Lunch',                     icon:'🥗', unit:'per person',  group:'Food & Beverage'},
  { id:'dinner',                label:'Dinner',                    icon:'🍖', unit:'per person',  group:'Food & Beverage'},
  { id:'breakfast',             label:'Breakfast',                 icon:'🍳', unit:'per person',  group:'Food & Beverage'},
  { id:'tea_coffee',            label:'Tea / Coffee Break',        icon:'☕', unit:'per session', group:'Food & Beverage'},
  { id:'cocktail',              label:'Cocktail / Welcome Drink',  icon:'🥂', unit:'per person',  group:'Food & Beverage'},
  { id:'minibar',               label:'Minibar',                   icon:'🍷', unit:'per item',    group:'Food & Beverage'},
  // ── Other services ────────────────────────────────────────────────────
  { id:'laundry',               label:'Laundry',                   icon:'👕', unit:'per piece',   group:'Other'          },
  { id:'transport',             label:'Airport Transfer',          icon:'🚌', unit:'per trip',    group:'Other'          },
  { id:'spa',                   label:'Spa & Wellness',            icon:'💆', unit:'per session', group:'Other'          },
  { id:'gym',                   label:'Gym',                       icon:'🏋️', unit:'per day',    group:'Other'          },
  { id:'swimming',              label:'Swimming Pool',             icon:'🏊', unit:'per day',     group:'Other'          },
  { id:'parking',               label:'Parking',                   icon:'🚗', unit:'per day',     group:'Other'          },
  { id:'other',                 label:'Other',                     icon:'📦', unit:'per unit',    group:'Other'          },
];
const isConfType = id => id === 'conference' || (typeof id === 'string' && id.startsWith('conference_'));
const svcMeta = id => SERVICE_TYPES.find(s => s.id === id) || { label: id, icon: '📦', unit: '' };

const PAYMENT_METHODS = [
  { id:'bank',       label:'Bank Transfer' },
  { id:'cash',       label:'Cash'          },
  { id:'mtn_momo',   label:'MTN MoMo'      },
  { id:'airtel',     label:'Airtel Money'  },
  { id:'visa',       label:'Visa Card'     },
  { id:'mastercard', label:'Mastercard'    },
  { id:'other',      label:'Other'         },
];

const SESSION_STATUS = {
  pending:   { color: T.warning, bg: T.warningPale, label: 'Pending'   },
  active:    { color: T.teal,    bg: T.tealPale,    label: 'Active'    },
  completed: { color: T.success, bg: T.successPale, label: 'Completed' },
  cancelled: { color: T.danger,  bg: T.dangerPale,  label: 'Cancelled' },
};

const fmtDate = d => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
// FIX: fmtDate above is for pure LocalDate fields (startDate, dueDate, etc.)
// and appends 'T00:00:00' to build a valid ISO string. LocalDateTime fields
// (paidAt, issuedAt) already come back from the backend WITH a time
// component (e.g. "2026-07-04T18:20:00") — appending 'T00:00:00' to those
// produces an invalid double-time string ("...T18:20:00T00:00:00"), which
// is why "Invalid Date" was showing up for payment/issue timestamps.
const fmtDateTime = d => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmt = n => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
const fmtDec = n => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

/* ─────────────────────────────────────────────────────────────────────────── */
/* Tax helpers                                                                 */
/* ─────────────────────────────────────────────────────────────────────────── */
const ACCOM_TYPES = new Set(['accommodation', 'room']);

/**
 * Sums a session's planned services into accommodation vs other-service
 * totals, then extracts the tax embedded in each using the RRA/EBM
 * sequential formulas.
 *
 * Accommodation (room charges):
 *   Step 1 — VAT = gross × vatRate / (100 + vatRate)
 *   Step 2 — TourismLevy = VAT × tourismRate / (100 + tourismRate)
 *
 * Other Services:
 *   VAT = gross × vatRate / (100 + vatRate)
 *
 * Used to give payment requests a real "Total Tax" figure pro-rated to
 * how much of the whole session this particular request represents.
 */
function computeSessionTaxBreakdown(guestDetails, property) {
  const { vatRate, tourismRate } = getTaxRates(property);
  let accomTotal = 0, otherTotal = 0;
  (guestDetails || []).forEach(gd => {
    (gd.services || []).forEach(s => {
      const amt = Number(s.plannedAmount || 0);
      if (ACCOM_TYPES.has(s.serviceType)) accomTotal += amt;
      else otherTotal += amt;
    });
  });

  // Accommodation: RRA two-step extraction
  //   Step 1: VAT        = gross × vatRate / (100 + vatRate)
  //   Step 2: Tourism    = (gross − VAT) × tourismRate / (100 + tourismRate)
  let accomTax = 0;
  if (accomTotal > 0 && vatRate > 0) {
    const accomVat     = accomTotal * vatRate / (100 + vatRate);
    const netOfVat     = accomTotal - accomVat;
    const accomTourism = tourismRate > 0 ? netOfVat * tourismRate / (100 + tourismRate) : 0;
    accomTax = accomVat + accomTourism;
  }

  // Other services: VAT only
  const otherTax = (otherTotal > 0 && vatRate > 0)
    ? otherTotal * vatRate / (100 + vatRate)
    : 0;

  return { grandTotal: accomTotal + otherTotal, totalTax: Math.round((accomTax + otherTax) * 100) / 100 };
}

/** Returns { vatRate, tourismRate } from property, with safe fallbacks */
function getTaxRates(property) {
  return {
    vatRate:     Number(property?.vatRate     ?? 18),
    tourismRate: Number(property?.tourismTaxRate ?? 3),
  };
}

/** Rate string for display: "18% VAT" or "3% Tourism + 18% VAT" (accommodation carries both) */
function taxLabel(serviceType, property) {
  const { vatRate, tourismRate } = getTaxRates(property);
  return ACCOM_TYPES.has(serviceType)
    ? `${tourismRate}% Tourism + ${vatRate}% VAT Inclusive`
    : `${vatRate}% VAT Inclusive`;
}

/**
 * Feature: VAT-exclusive / VAT / VAT-inclusive totals on Proforma invoices.
 *
 * Reuses the SAME per-line rate logic already driving each row's "Tax"
 * label (splitAccommodationTax — VAT extracted from the gross amount, then
 * Tourism Levy extracted from what's left, exactly as the Tax Invoice
 * already does). Does not change any tax rate or calculation rule — only
 * aggregates the existing rule across lines so it can be displayed as three
 * clear totals instead of one blended figure.
 *
 * @param lines        array of line items
 * @param isAccomFn    (line) => bool — true if the line is taxed as accommodation (Tourism + VAT)
 * @param amountFn     (line) => number — the line's VAT-inclusive gross amount
 */
function sumVatBreakdown(lines, { vatRate, tourismRate }, isAccomFn, amountFn, authoritativeGrossTotal) {
  let vat = 0, tourism = 0, summedGross = 0;
  (lines || []).forEach(line => {
    const gross = Number(amountFn(line)) || 0;
    summedGross += gross;
    const split = splitAccommodationTax(gross, isAccomFn(line) ? tourismRate : 0, vatRate);
    vat     += split.vat;
    tourism += split.tourismTax;
  });
  // The grand/inclusive total is always the pre-existing authoritative figure
  // (whatever the caller already displayed) — never a re-summed value — so
  // this breakdown can never disagree with the total by a rounding cent.
  // net/exclusive is derived by subtracting the two taxes from that figure.
  const inclusiveTotal = authoritativeGrossTotal != null ? Number(authoritativeGrossTotal) || 0 : summedGross;
  const exclusiveTotal = round2(inclusiveTotal - vat - tourism);
  return { exclusiveTotal, vatAmount: round2(vat), tourismAmount: round2(tourism), inclusiveTotal };
}
function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }

/* ─────────────────────────────────────────────────────────────────────────── */
/* Micro UI components                                                         */
/* ─────────────────────────────────────────────────────────────────────────── */
const IST = {
  width: '100%', border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 10px',
  fontSize: 12, fontFamily: FONTS.body, color: T.navy, outline: 'none', boxSizing: 'border-box',
};
const Inp = ({ value, onChange, type = 'text', placeholder = '', rows, disabled }) =>
  rows
    ? <textarea value={value} placeholder={placeholder} rows={rows} disabled={disabled}
        onChange={e => onChange(e.target.value)} style={{ ...IST, resize: 'vertical', opacity: disabled ? .6 : 1 }} />
    : <input type={type} value={value} placeholder={placeholder} disabled={disabled}
        onChange={e => onChange(e.target.value)} style={{ ...IST, opacity: disabled ? .6 : 1 }} />;

const Sel = ({ value, onChange, children, disabled }) =>
  <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
    style={{ ...IST, opacity: disabled ? .6 : 1 }}>{children}</select>;

const Fld = ({ label, required, hint, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: T.muted,
      letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>
      {label}{required && <span style={{ color: T.danger }}> *</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 10, color: T.muted, marginTop: 3 }}>{hint}</p>}
  </div>
);

function SBadge({ status }) {
  const c = SESSION_STATUS[status] || { color: T.muted, bg: T.surface, label: status };
  return (
    <span style={{ fontSize: 9, fontWeight: 700, color: c.color, background: c.bg,
      padding: '3px 10px', borderRadius: 99, textTransform: 'uppercase', letterSpacing: .5 }}>
      {c.label}
    </span>
  );
}

function GuestStatusBadge({ status }) {
  const map = {
    pending:     { color: T.muted,   bg: T.surface,    label: 'Pending'     },
    reserved:    { color: T.warning, bg: T.warningPale, label: 'Reserved'   },
    checked_in:  { color: T.teal,    bg: T.tealPale,   label: 'In-House'   },
    checked_out: { color: T.success, bg: T.successPale, label: 'Checked Out' },
  };
  const c = map[status] || map.pending;
  return (
    <span style={{ fontSize: 9, fontWeight: 700, color: c.color, background: c.bg,
      padding: '2px 8px', borderRadius: 99, textTransform: 'uppercase', letterSpacing: .4 }}>
      {c.label}
    </span>
  );
}

function SectionHead({ icon, title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 12, paddingBottom: 8, borderBottom: `2px solid ${T.teal}20` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: T.navy }}>{title}</span>
      </div>
      {action}
    </div>
  );
}

function SmBtn({ children, onClick, color = T.teal, bg, border, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ background: bg || `${color}15`, border: `1px solid ${border || color + '40'}`,
        color, borderRadius: 7, padding: '5px 12px', fontSize: 11, fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: FONTS.body, opacity: disabled ? .6 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {children}
    </button>
  );
}

function PrimaryBtn({ children, onClick, disabled, loading, variant = 'navy', full }) {
  const colors = {
    navy:    { bg: T.navy,    text: T.white  },
    teal:    { bg: T.teal,    text: T.white  },
    success: { bg: T.success, text: T.white  },
    danger:  { bg: T.danger,  text: T.white  },
  };
  const c = colors[variant] || colors.navy;
  return (
    <button onClick={onClick} disabled={disabled || loading}
      style={{ background: disabled || loading ? T.muted : c.bg, color: c.text, border: 'none',
        borderRadius: 9, padding: '11px 20px', fontSize: 13, fontWeight: 700,
        cursor: disabled || loading ? 'not-allowed' : 'pointer', fontFamily: FONTS.body,
        width: full ? '100%' : 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
      {loading ? '⏳ Saving…' : children}
    </button>
  );
}

function Toast({ msg }) {
  return (
    <div style={{ position: 'fixed', top: 70, right: 24, zIndex: 1999, background: T.navy,
      color: T.white, borderRadius: 10, padding: '11px 18px', fontSize: 13, fontWeight: 600,
      boxShadow: '0 8px 32px rgba(0,0,0,.25)' }}>{msg}</div>
  );
}

function Modal({ title, onClose, children, wide, extraWide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1500,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%',
        maxWidth: extraWide ? 860 : wide ? 700 : 500, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 32px 80px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0,
          background: T.white, zIndex: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: T.navy }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18,
            color: T.muted, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* PRINT BUILDERS                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */

function buildHeader(property, title, date) {
  const logoUrl = resolveAssetUrl(property?.logoUrl);
  const addr = [property?.addressLine1, property?.addressLine2, property?.city]
    .filter(Boolean).join(', ');
  // Left: property info. Right: logo (like InvoicePrint). Title is centered below.
  return `
    <table style="width:100%;margin-bottom:20px;border-collapse:collapse">
      <tr>
        <td style="vertical-align:top;width:60%">
          <div style="font-size:18px;font-weight:700;color:#0e2240;margin-bottom:2px">${property?.name || ''}</div>
          ${property?.type ? `<div style="font-size:10px;font-weight:700;color:#1A7F8E;letter-spacing:.6px;text-transform:uppercase;margin-bottom:4px">${property.type}</div>` : ''}
          <div style="font-size:10px;color:#64748B;line-height:1.7">
            ${addr ? addr + '<br>' : ''}
            ${property?.phone ? 'TEL: ' + property.phone + '<br>' : ''}
            ${property?.email ? 'E-mail: ' + property.email + '<br>' : ''}
            ${property?.tin ? 'TIN &amp; TVA: ' + property.tin : ''}
          </div>
        </td>
        <td style="vertical-align:top;text-align:right">
          ${logoUrl
            ? `<img src="${logoUrl}" alt="Logo" crossorigin="anonymous" style="max-height:80px;max-width:180px;object-fit:contain"/>`
            : `<div style="font-size:22px;font-weight:700;color:#1A7F8E">${property?.name || ''}</div>`}
        </td>
      </tr>
    </table>
    <div style="text-align:center;margin:4px 0 20px">
      <span style="font-size:16px;font-weight:800;color:#0e2240;text-decoration:underline;letter-spacing:1.2px;text-transform:uppercase">${title}</span>
      <div style="font-size:10px;color:#64748B;margin-top:4px">Date: ${date}</div>
    </div>`;
}

function buildFooter(property) {
  if (!property?.invoiceFooterText) return '';
  return `
    <div style="border-top:1px solid #0e2240;padding-top:14px;margin-top:8px">
      <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;margin-bottom:6px;color:#0e2240">
        ALL PAYMENTS TO BE MADE TO THE FOLLOWING BANK ACCOUNTS. PLEASE SEND PROOF OF PAYMENT TO:
      </div>
      ${property.email ? `<div style="font-size:11px;color:#1A7F8E;margin-bottom:8px">${property.email}</div>` : ''}
      <div style="font-size:10px;color:#333;line-height:1.9;white-space:pre-line">${property.invoiceFooterText}</div>
    </div>`;
}

function buildSignature(property) {
  const todayL = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return `
    <table style="width:100%;margin-top:32px">
      <tr>
        <td style="font-size:11px;color:#64748B">Done at ${property?.city || 'Hotel'}, ${todayL}</td>
        <td style="text-align:right">
          <div style="font-size:11px;font-weight:700;color:#0e2240">Authorized Signature</div>
          <div style="width:160px;height:50px;margin-left:auto;margin-top:8px;border-bottom:1px solid #0e2240"></div>
        </td>
      </tr>
    </table>`;
}

// ── Individual Proforma ──────────────────────────────────────────────────────
// Phase 9: show package name + inclusive items only when service has a package.
//          Individual invoices stay clean — no package noise for plain services.
function buildIndividualProformaHtml({ session, guest, services, total, property }) {
  const cur   = session?.currency || property?.currency || 'RWF';
  const { vatRate, tourismRate } = getTaxRates(property);
  const words = amountInWords(total, cur);
  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const terms = property?.proformaTermsText
    || '& 50% of the total amount must be paid to confirm the reservation and the balance settled on the day of check in.\n& No reservation will be held until an LPO is received by the hotel from clients with contracts.\n& Cancellations will only be accepted in writing. Cancellations done 5 days or less from date of arrival shall be charged in full.\n& Accepted methods of payment: Credit Cards, Cash, Electronic Transfers';

  const rows = (services || []).map((s, i) => {
    const tLabel = ACCOM_TYPES.has(s.serviceType)
      ? `${tourismRate}% Tourism + ${vatRate}% VAT Incl.`
      : `${vatRate}% VAT Incl.`;
    const pkg = s.packageName ? String(s.packageName).trim() : null;
    let inclusives = [];
    if (s.packageInclusives) {
      try { inclusives = JSON.parse(s.packageInclusives); }
      catch { inclusives = String(s.packageInclusives).split(',').map(x => x.trim()).filter(Boolean); }
    }
    // Only show inclusive badge row when this service actually has a package
    const inclusiveHtml = (pkg && inclusives.length)
      ? `<div style="margin-top:3px;padding-left:10px;font-size:9px;color:#374151;line-height:1.8">
           <em>Includes:</em> ${inclusives.map(item =>
             `<span style="display:inline-block;background:#F0F9FF;border:1px solid #BAE6FD;color:#0369A1;border-radius:99px;padding:1px 6px;margin:1px 2px">${item}</span>`
           ).join('')}
         </div>` : '';
    return `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f8fafc'}">
        <td style="padding:7px 10px;border:1px solid #ddd;font-size:11px;vertical-align:top">
          <div style="font-weight:600;color:#0e2240">${s.serviceLabel || s.serviceType}</div>
          ${pkg ? `<div style="font-size:10px;color:#1A7A4A;font-weight:600;margin-top:2px">&#8627; ${pkg}</div>${inclusiveHtml}` : ''}
        </td>
        <td style="padding:7px 10px;border:1px solid #ddd;font-size:11px;text-align:right;vertical-align:top">${fmt(s.quantity)}</td>
        <td style="padding:7px 10px;border:1px solid #ddd;font-size:11px;text-align:right;vertical-align:top">${s.unit || '—'}</td>
        <td style="padding:7px 10px;border:1px solid #ddd;font-size:11px;text-align:right;vertical-align:top">${fmt(s.unitPrice)}</td>
        <td style="padding:7px 10px;border:1px solid #ddd;font-size:10px;text-align:center;color:#64748B;vertical-align:top">${tLabel}</td>
        <td style="padding:7px 10px;border:1px solid #ddd;font-size:11px;text-align:right;font-weight:700;vertical-align:top">${fmt(s.plannedAmount)}</td>
      </tr>`;
  }).join('');

  const { exclusiveTotal, vatAmount, tourismAmount, inclusiveTotal } =
    sumVatBreakdown(services, { vatRate, tourismRate }, s => ACCOM_TYPES.has(s.serviceType), s => s.plannedAmount, total);

  return `
    ${buildHeader(property, 'PROFORMA INVOICE', today)}
    <table style="width:100%;border-collapse:collapse;margin-bottom:20px;border:1px solid #0e2240">
      <tr>
        <td style="padding:6px 10px;border:1px solid #0e2240;width:50%;font-size:11px"><b>Guest:</b> ${guest?.displayName || '—'}</td>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Company:</b> ${session?.companyName || '—'}</td>
      </tr>
      <tr>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Date:</b> ${today}</td>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Session:</b> ${session?.sessionName || '—'}</td>
      </tr>
      <tr>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Arrival:</b> ${fmtDate(session?.startDate)}</td>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Departure:</b> ${fmtDate(session?.endDate)}</td>
      </tr>
    </table>
    <table style="width:100%;border-collapse:collapse">
      <thead>
        <tr style="background:#0e2240">
          <th style="padding:8px 10px;color:#fff;font-size:11px;font-weight:700;text-align:left">Description</th>
          <th style="padding:8px 10px;color:#fff;font-size:11px;font-weight:700;text-align:right">Qty</th>
          <th style="padding:8px 10px;color:#fff;font-size:11px;font-weight:700;text-align:right">Unit</th>
          <th style="padding:8px 10px;color:#fff;font-size:11px;font-weight:700;text-align:right">Unit Price</th>
          <th style="padding:8px 10px;color:#fff;font-size:11px;font-weight:700;text-align:center">Tax</th>
          <th style="padding:8px 10px;color:#fff;font-size:11px;font-weight:700;text-align:right">Total (${cur})</th>
        </tr>
      </thead>
      <tbody>${rows || '<tr><td colspan="6" style="padding:16px;text-align:center;font-size:11px;color:#999">No services planned</td></tr>'}</tbody>
      <tfoot>
        <tr>
          <td colspan="5" style="padding:7px 10px;font-size:11px;font-weight:700;border:1px solid #0e2240;background:#f8fafc">VAT Exclusive Total</td>
          <td style="padding:7px 10px;font-size:11px;font-weight:700;text-align:right;border:1px solid #0e2240;background:#f8fafc">${fmt(exclusiveTotal)}</td>
        </tr>
        <tr>
          <td colspan="5" style="padding:6px 10px;font-size:11px;border:1px solid #0e2240;background:#f8fafc">VAT Amount (${vatRate}%)</td>
          <td style="padding:6px 10px;font-size:11px;text-align:right;border:1px solid #0e2240;background:#f8fafc">${fmt(vatAmount)}</td>
        </tr>
        ${tourismAmount > 0 ? `
        <tr>
          <td colspan="5" style="padding:6px 10px;font-size:10px;color:#64748B;border:1px solid #0e2240;background:#f8fafc">&#8627; incl. Tourism Levy (${tourismRate}%)</td>
          <td style="padding:6px 10px;font-size:10px;color:#64748B;text-align:right;border:1px solid #0e2240;background:#f8fafc">${fmt(tourismAmount)}</td>
        </tr>` : ''}
        <tr>
          <td colspan="5" style="padding:8px 10px;font-size:12px;font-weight:800;border:1px solid #0e2240;background:#0e2240;color:#fff">TOTAL (VAT Inclusive)</td>
          <td style="padding:8px 10px;font-size:13px;font-weight:800;text-align:right;border:1px solid #0e2240;background:#0e2240;color:#C9A84C">${fmt(inclusiveTotal)}</td>
        </tr>
      </tfoot>
    </table>
    <div style="margin:8px 0 16px;font-size:10px;color:#64748B;font-style:italic">We say the sum of <strong style="color:#0e2240">${words}</strong>.</div>
    <div style="margin-bottom:20px">
      <div style="font-size:11px;font-weight:700;text-decoration:underline;margin-bottom:6px;color:#0e2240">Terms and Conditions</div>
      <div style="font-size:10px;color:#333;line-height:1.7;white-space:pre-line">${terms}</div>
    </div>
    ${buildFooter(property)}
    ${buildSignature(property)}`;
}

// ── Corporate Proforma ───────────────────────────────────────────────────────
// Columns (matching hotel reference): Service/Package | qty | Days | Unit Price | Tax (incl.) | Total (frw)
// Participant count shown as badge only — no name list (avoids long documents)
function buildCorporateProformaHtml({ session, guests, serviceLines, grandTotal, guestCount, registeredParticipants, property }) {
  const cur   = session?.currency || property?.currency || 'RWF';
  const { vatRate, tourismRate } = getTaxRates(property);
  const words = amountInWords(grandTotal, cur);
  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const terms = property?.proformaTermsText
    || '& 50% of the total amount must be paid to confirm the reservation and the balance settled before departure.\n& No reservation will be held until payment is received.\n& Cancellations must be submitted in writing at least 5 days prior to arrival.\n& Accepted methods of payment: Bank Transfer, Mobile Money, Credit Cards.';

  const participantCount = guestCount ?? (guests?.length ?? 0);

  // 6-column rows: Service/Package | qty | Days | Unit Price | Tax | Total
  const rows = (serviceLines || []).map((l, i) => {
    const taxLabel = ACCOM_TYPES.has(l.serviceType)
      ? `${tourismRate}%<br>Tourism<br>+ ${vatRate}%<br>VAT`
      : `${vatRate}%<br>VAT`;
    const qty  = l.numParticipants ?? participantCount;
    const days = l.numDays ?? 1;
    const pkg  = l.packageName ? String(l.packageName).trim() : null;
    let inclusives = [];
    if (l.packageInclusives) {
      try { inclusives = JSON.parse(l.packageInclusives); }
      catch { inclusives = String(l.packageInclusives).split(',').map(s => s.trim()).filter(Boolean); }
    }
    const inclusiveHtml = (pkg && inclusives.length)
      ? `<div style="margin-top:5px;padding-left:4px">${inclusives.map(item =>
           `<span style="display:inline-block;background:#F0F9FF;border:1px solid #BAE6FD;color:#0369A1;border-radius:99px;padding:1px 7px;margin:1px 2px;font-size:9px">${item}</span>`
         ).join('')}</div>` : '';
    return `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f8fafc'}">
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:11px;vertical-align:top">
          <div style="font-weight:700;color:#0e2240">${l.serviceLabel || l.serviceType}</div>
          ${pkg ? `<div style="font-size:10px;color:#1A7A4A;font-weight:600;margin-top:2px">&#8627; ${pkg}</div>${inclusiveHtml}` : ''}
        </td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:12px;font-weight:700;text-align:right;vertical-align:top">${qty}</td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:12px;font-weight:700;text-align:right;vertical-align:top">${days}</td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:11px;text-align:right;vertical-align:top">${fmt(l.unitPrice)}</td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:9px;text-align:center;color:#64748B;line-height:1.4;vertical-align:top">${taxLabel}</td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:12px;font-weight:700;text-align:right;vertical-align:top">${fmt(l.totalAmount)}</td>
      </tr>`;
  }).join('');

  const { exclusiveTotal, vatAmount, tourismAmount, inclusiveTotal } =
    sumVatBreakdown(serviceLines, { vatRate, tourismRate }, l => ACCOM_TYPES.has(l.serviceType), l => l.totalAmount, grandTotal);

  return `
    ${buildHeader(property, 'CORPORATE PROFORMA INVOICE', today)}
    <table style="width:100%;border-collapse:collapse;margin-bottom:6px;border:1px solid #0e2240">
      <tr>
        <td style="padding:6px 10px;border:1px solid #0e2240;width:50%;font-size:11px"><b>Company:</b> ${session?.companyName || '—'}</td>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Arrival Date:</b> ${fmtDate(session?.startDate)}</td>
      </tr>
      <tr>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Session:</b> ${session?.sessionName || '—'}</td>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Date:</b> ${today}</td>
      </tr>
      <tr>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Period:</b> ${fmtDate(session?.startDate)} — ${fmtDate(session?.endDate)}</td>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px">
          <b>Number of Participants:</b>&nbsp;
          <span style="background:#C9A84C;color:#0e2240;font-weight:800;border-radius:99px;padding:1px 12px;font-size:12px">${participantCount}</span>
          ${registeredParticipants > 0 && registeredParticipants !== participantCount ? `<span style="font-size:10px;color:#64748B;margin-left:8px">(${registeredParticipants} registered individually — included in the total)</span>` : ''}
        </td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse">
      <thead>
        <tr style="background:#0e2240">
          <th style="padding:9px 10px;color:#fff;font-size:11px;font-weight:700;text-align:left;width:40%">Service / Package</th>
          <th style="padding:9px 10px;color:#fff;font-size:11px;font-weight:700;text-align:right;width:7%">qty</th>
          <th style="padding:9px 10px;color:#fff;font-size:11px;font-weight:700;text-align:right;width:7%">Days</th>
          <th style="padding:9px 10px;color:#fff;font-size:11px;font-weight:700;text-align:right;width:14%">Unit Price</th>
          <th style="padding:9px 10px;color:#fff;font-size:11px;font-weight:700;text-align:center;width:10%">Tax</th>
          <th style="padding:9px 10px;color:#fff;font-size:11px;font-weight:700;text-align:right;width:16%">Total Price (frw)</th>
        </tr>
      </thead>
      <tbody>${rows || '<tr><td colspan="6" style="padding:16px;text-align:center;color:#999;font-size:11px">No services planned</td></tr>'}</tbody>
      <tfoot>
        <tr style="background:#f8fafc">
          <td colspan="5" style="padding:8px 10px;font-size:11px;font-weight:700;border:1px solid #ddd">VAT Exclusive Total</td>
          <td style="padding:8px 10px;font-size:11px;font-weight:700;text-align:right;border:1px solid #ddd">${fmt(exclusiveTotal)}</td>
        </tr>
        <tr style="background:#f8fafc">
          <td colspan="5" style="padding:6px 10px;font-size:11px;border:1px solid #ddd">VAT Amount (${vatRate}%)</td>
          <td style="padding:6px 10px;font-size:11px;text-align:right;border:1px solid #ddd">${fmt(vatAmount)}</td>
        </tr>
        ${tourismAmount > 0 ? `
        <tr style="background:#f8fafc">
          <td colspan="5" style="padding:6px 10px;font-size:10px;color:#64748B;border:1px solid #ddd">&#8627; incl. Tourism Levy (${tourismRate}%)</td>
          <td style="padding:6px 10px;font-size:10px;color:#64748B;text-align:right;border:1px solid #ddd">${fmt(tourismAmount)}</td>
        </tr>` : ''}
        <tr style="background:#0e2240">
          <td colspan="5" style="padding:10px 12px;color:#fff;font-size:12px;font-weight:800">TOTAL (VAT Inclusive)</td>
          <td style="padding:10px 12px;color:#C9A84C;font-size:14px;font-weight:800;text-align:right">${fmt(inclusiveTotal)}</td>
        </tr>
      </tfoot>
    </table>

    <div style="margin:14px 0 8px;font-size:10px;color:#64748B;font-style:italic">We say the sum of <strong style="color:#0e2240">${words}</strong>.</div>
    <div style="margin-bottom:20px">
      <div style="font-size:11px;font-weight:700;text-decoration:underline;margin-bottom:6px;color:#0e2240">Terms and Conditions</div>
      <div style="font-size:10px;color:#333;line-height:1.7;white-space:pre-line">${terms}</div>
    </div>
    ${buildFooter(property)}
    ${buildSignature(property)}`;
}

// ── Payment Request ──────────────────────────────────────────────────────────
function buildPaymentRequestHtml({ session, paymentRequest, installments, guests, property }) {
  const cur       = paymentRequest?.currency || session?.currency || property?.currency || 'RWF';
  const requested = Number(paymentRequest?.requestedAmount || 0);
  const paid      = Number(paymentRequest?.amountPaid || 0);
  const due       = requested - paid;
  const words     = amountInWords(requested, cur);
  const todayL    = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const logoUrl   = resolveAssetUrl(property?.logoUrl);
  const logoTag   = logoUrl
    ? `<img src="${logoUrl}" style="max-height:80px;max-width:160px;object-fit:contain"/>`
    : `<div style="font-size:28px;font-weight:700;color:#1A7F8E">${property?.name || ''}</div>`;
  const instRows  = (installments || []).map(i => `
    <tr>
      <td style="padding:7px 12px;border:1px solid #ddd;font-size:11px;color:#1A7A4A">✓ Payment received — ${fmtDateTime(i.paidAt)}${i.reference ? ' (Ref: ' + i.reference + ')' : ''}</td>
      <td style="padding:7px 12px;border:1px solid #ddd;font-size:11px;text-align:right;color:#1A7A4A">(${fmt(i.amount)})</td>
    </tr>`).join('');
  const bankFooter = property?.invoiceFooterText || '';
  const signerRole = property?.paymentRequestSignerRole || 'Manager';

  // Body paragraph is fully dynamic per business: render the property's own
  // template (Property Setup → Invoicing & Tax) with real merge-field
  // values, falling back to the built-in wording if none is configured.
  // Total tax is the real embedded tax across the session's planned
  // services (accommodation taxed at tourismRate, everything else at
  // vatRate), pro-rated to how much of the whole session this request
  // represents — not a hardcoded 0.
  const { grandTotal: sessionGrandTotal, totalTax: sessionTotalTax } = computeSessionTaxBreakdown(guests, property);
  const requestTaxShare = sessionGrandTotal > 0 ? (requested / sessionGrandTotal) * sessionTotalTax : 0;
  const bodyTemplate = property?.paymentRequestBodyTemplate || DEFAULT_PAYMENT_REQUEST_BODY;
  const bodyText = renderTemplate(bodyTemplate, {
    company:       session?.companyName || 'The Company',
    property:      property?.name || '',
    amountWords:   words,
    amountNumeric: fmt(requested),
    currency:      cur,
    totalTax:      fmt(requestTaxShare),
    reason:        session?.sessionName || 'services rendered',
    invoiceNumber: paymentRequest?.requestNumber || '—',
  });

  return `
    <table style="width:100%;margin-bottom:48px;border-collapse:collapse">
      <tr>
        <td style="vertical-align:top;width:60%">
          <div style="font-size:13px;font-weight:800;color:#1A7F8E;line-height:1.4">${property?.name || ''}</div>
          <div style="font-size:10px;color:#333;line-height:1.8">${[property?.addressLine1, property?.addressLine2, property?.city].filter(Boolean).join(', ')}<br>${property?.phone ? 'TEL: ' + property.phone + '<br>' : ''}${property?.email ? 'E-mail: <span style="color:#1A7F8E">' + property.email + '</span><br>' : ''}${property?.tin ? 'TIN: ' + property.tin : ''}</div>
        </td>
        <td style="vertical-align:middle;text-align:right">${logoTag}</td>
      </tr>
    </table>
    <div style="text-align:center;margin-bottom:36px">
      <span style="font-size:13px;font-weight:800;text-decoration:underline;letter-spacing:1px;text-transform:uppercase">Request for Payment</span>
      ${paymentRequest?.requestNumber ? `<div style="font-size:10px;color:#64748B;margin-top:4px">Ref: ${paymentRequest.requestNumber}</div>` : ''}
    </div>
    <p style="font-size:12px;margin-bottom:20px;line-height:1.9">${bodyText}</p>
    <p style="font-size:12px;margin-bottom:32px;line-height:1.9">We say the sum of <span style="text-decoration:underline;font-weight:700">${words} (${fmt(requested)} ${cur})</span>.</p>
    <table style="width:100%;border-collapse:collapse;margin-bottom:28px">
      <thead><tr style="background:#0e2240">
        <th style="padding:8px 12px;color:#fff;font-size:11px;font-weight:700;text-align:left">Description</th>
        <th style="padding:8px 12px;color:#fff;font-size:11px;font-weight:700;text-align:right">${cur}</th>
      </tr></thead>
      <tbody>
        <tr><td style="padding:7px 12px;border:1px solid #ddd;font-size:11px">Total Requested — ${session?.sessionName || ''}</td><td style="padding:7px 12px;border:1px solid #ddd;font-size:11px;text-align:right;font-weight:600">${fmt(requested)}</td></tr>
        ${instRows}
        <tr style="background:${due > 0 ? '#FFFBEB' : '#EAFAF1'}">
          <td style="padding:9px 12px;border:1px solid #0e2240;font-size:12px;font-weight:800">${due > 0 ? 'Outstanding Balance' : 'FULLY PAID'}</td>
          <td style="padding:9px 12px;border:1px solid #0e2240;font-size:13px;font-weight:800;text-align:right;color:${due > 0 ? '#D97706' : '#1A7A4A'}">${due > 0 ? fmt(due) : fmt(paid)}</td>
        </tr>
      </tbody>
    </table>
    ${paymentRequest?.dueDate ? `<p style="font-size:11px;margin-bottom:16px"><strong>Payment Due:</strong> ${fmtDate(paymentRequest.dueDate)}</p>` : ''}
    ${bankFooter ? `<div style="margin-bottom:32px;font-size:10px;color:#333;line-height:1.8;white-space:pre-line">${bankFooter}</div>` : ''}
    ${property?.proformaTermsText ? `<div style="margin-bottom:28px"><div style="font-size:10px;font-weight:700;text-decoration:underline;margin-bottom:6px">Terms and Conditions</div><div style="font-size:10px;color:#333;line-height:1.7;white-space:pre-line">${property.proformaTermsText}</div></div>` : ''}
    <div style="margin-top:40px">
      <div style="font-size:11px;margin-bottom:4px">Done at ${property?.city || 'Hotel'}: ${todayL}</div>
      ${property?.managerName ? `<div style="font-size:11px;margin-bottom:2px">Authorized by: <strong>${property.managerName}</strong></div>` : ''}
      <div style="font-size:11px;margin-bottom:12px">Function: ${signerRole}</div>
      <div style="width:120px;height:80px;border:1px dashed #ddd;display:inline-flex;align-items:center;justify-content:center;font-size:9px;color:#94A3B8;margin-top:8px">Stamp</div>
    </div>`;
}

// ── Final Corporate Invoice ──────────────────────────────────────────────────
function buildFinalInvoiceHtml({ session, guests, totalParticipants, registeredParticipants, invoiceLines, subtotal, totalTax, grandTotal, totalPaid, outstanding, vatRate, tourismTaxRate, paymentRequests, currency, property }) {
  const cur    = currency || session?.currency || property?.currency || 'RWF';
  const words  = amountInWords(grandTotal, cur);
  const today  = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const todayL = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const numParticipants = totalParticipants ?? guests?.length ?? 0;
  const registeredNote  = registeredParticipants != null && registeredParticipants > 0 && registeredParticipants !== numParticipants
    ? ` <span style="font-weight:400;color:#64748B">(${registeredParticipants} registered individually)</span>` : '';

  // 7-column rows matching the PDF: Service | qty | Days | Unit Price | Tax | Tax Amt | Total
  const rows = (invoiceLines || []).map((l, i) => {
    const taxLabel = l.isAccommodation
      ? `${tourismTaxRate}%<br>Tourism<br>+ ${vatRate}%<br>VAT`
      : `${vatRate}%<br>VAT`;
    const qty  = l.numParticipants ?? numParticipants;
    const days = l.numDays ?? 1;
    const pkg  = l.packageName ? String(l.packageName).trim() : null;
    let inclusives = [];
    if (l.packageInclusives) {
      try { inclusives = JSON.parse(l.packageInclusives); }
      catch { inclusives = String(l.packageInclusives).split(',').map(s => s.trim()).filter(Boolean); }
    }
    const inclusiveHtml = (pkg && inclusives.length)
      ? `<div style="margin-top:4px;padding-left:4px">${inclusives.map(item =>
           `<span style="display:inline-block;background:#F0F9FF;border:1px solid #BAE6FD;color:#0369A1;border-radius:99px;padding:1px 6px;margin:1px 2px;font-size:9px">${item}</span>`
         ).join('')}</div>` : '';
    return `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f8fafc'}">
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:11px;vertical-align:top">
          <div style="font-weight:700;color:#0e2240">${l.serviceLabel || l.serviceType}</div>
          ${pkg ? `<div style="font-size:10px;color:#1A7A4A;font-weight:600;margin-top:2px">&#8627; ${pkg}</div>${inclusiveHtml}` : ''}
        </td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:12px;font-weight:700;text-align:right;vertical-align:top">${qty}</td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:12px;font-weight:700;text-align:right;vertical-align:top">${days}</td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:11px;text-align:right;vertical-align:top">${fmtDec(l.unitPrice)}</td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:9px;text-align:center;color:#64748B;line-height:1.4;vertical-align:top">${taxLabel}</td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:10px;text-align:right;color:#666;vertical-align:top">${fmtDec(l.taxAmount)}</td>
        <td style="padding:8px 10px;border:1px solid #ddd;font-size:12px;font-weight:700;text-align:right;vertical-align:top">${fmtDec(l.totalWithTax)}</td>
      </tr>`;
  }).join('');

  // Re-derive Tourism Levy and VAT split for the footer tax breakdown
  let tourismTaxTotal = 0, vatTaxTotal = 0;
  const vr = Number(vatRate || 0);
  const tr = Number(tourismTaxRate || 0);
  (invoiceLines || []).forEach(l => {
    const gross = Number(l.totalWithTax || 0);
    if (gross <= 0) return;
    if (l.isAccommodation) {
      const vatAmt   = vr > 0 ? gross * vr / (100 + vr) : 0;
      const netOfVat = gross - vatAmt;
      const tourAmt  = (tr > 0 && netOfVat > 0) ? netOfVat * tr / (100 + tr) : 0;
      tourismTaxTotal += tourAmt;
      vatTaxTotal     += vatAmt;
    } else {
      vatTaxTotal += Number(l.taxAmount || 0);
    }
  });

  const paymentRows = (paymentRequests || []).map(pr => `
    <tr>
      <td style="padding:6px 10px;border:1px solid #ddd;font-size:11px">${pr.requestNumber}</td>
      <td style="padding:6px 10px;border:1px solid #ddd;font-size:11px">${fmtDateTime(pr.issuedAt)}</td>
      <td style="padding:6px 10px;border:1px solid #ddd;font-size:11px;text-align:right">${fmtDec(pr.requestedAmount)}</td>
      <td style="padding:6px 10px;border:1px solid #ddd;font-size:11px;text-align:right;color:#1A7A4A">${fmtDec(pr.amountPaid || 0)}</td>
      <td style="padding:6px 10px;border:1px solid #ddd;font-size:11px;text-align:center">
        <span style="font-size:9px;font-weight:700;color:${pr.status === 'paid' ? '#1A7A4A' : '#E07B00'};background:${pr.status === 'paid' ? '#EAF7EE' : '#FEF5E7'};padding:2px 8px;border-radius:99px;text-transform:uppercase">${pr.status}</span>
      </td>
    </tr>`).join('');

  return `
    ${buildHeader(property, 'CORPORATE TAX INVOICE', today)}
    <div style="margin-bottom:4px;font-size:10px;color:#64748B;text-align:right">Invoice # CORP-${session?.id?.substring(0, 8).toUpperCase() || '—'}</div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:16px;border:1px solid #0e2240">
      <tr>
        <td style="padding:6px 10px;border:1px solid #0e2240;width:50%;font-size:11px"><b>Bill To:</b> ${session?.companyName || '—'}</td>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Session:</b> ${session?.sessionName || '—'}</td>
      </tr>
      <tr>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Period:</b> ${fmtDate(session?.startDate)} — ${fmtDate(session?.endDate)}</td>
        <td style="padding:6px 10px;border:1px solid #0e2240;font-size:11px"><b>Participants:</b> ${numParticipants}${registeredNote}</td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;margin-bottom:8px">
      <thead>
        <tr style="background:#0e2240">
          <th style="padding:8px 10px;color:#fff;font-size:10px;font-weight:700;text-align:left;width:36%">Service / Package</th>
          <th style="padding:8px 10px;color:#fff;font-size:10px;font-weight:700;text-align:right;width:6%">qty</th>
          <th style="padding:8px 10px;color:#fff;font-size:10px;font-weight:700;text-align:right;width:6%">Days</th>
          <th style="padding:8px 10px;color:#fff;font-size:10px;font-weight:700;text-align:right;width:12%">Unit Price</th>
          <th style="padding:8px 10px;color:#fff;font-size:10px;font-weight:700;text-align:center;width:10%">Tax</th>
          <th style="padding:8px 10px;color:#fff;font-size:10px;font-weight:700;text-align:right;width:13%">Tax Amt</th>
          <th style="padding:8px 10px;color:#fff;font-size:10px;font-weight:700;text-align:right;width:17%">Total (RWF)</th>
        </tr>
      </thead>
      <tbody>${rows || '<tr><td colspan="7" style="padding:16px;text-align:center;color:#999">No services</td></tr>'}</tbody>
      <tfoot>
        <tr style="background:#f8fafc">
          <td colspan="5" style="padding:7px 10px;font-size:11px;font-weight:700;border:1px solid #ddd">Subtotal (excl. tax)</td>
          <td colspan="2" style="padding:7px 10px;font-size:11px;font-weight:700;text-align:right;border:1px solid #ddd">${fmtDec(subtotal)}</td>
        </tr>
        ${tourismTaxTotal > 0 ? `
        <tr style="background:#f8fafc">
          <td colspan="5" style="padding:6px 10px;font-size:11px;border:1px solid #ddd">Tourism Levy (${tourismTaxRate}%)</td>
          <td colspan="2" style="padding:6px 10px;font-size:11px;text-align:right;border:1px solid #ddd">${fmtDec(tourismTaxTotal)}</td>
        </tr>` : ''}
        ${vatTaxTotal > 0 ? `
        <tr style="background:#f8fafc">
          <td colspan="5" style="padding:6px 10px;font-size:11px;border:1px solid #ddd">VAT (${vatRate}%)</td>
          <td colspan="2" style="padding:6px 10px;font-size:11px;text-align:right;border:1px solid #ddd">${fmtDec(vatTaxTotal)}</td>
        </tr>` : ''}
        <tr style="background:#f8fafc">
          <td colspan="5" style="padding:7px 10px;font-size:11px;font-weight:700;border:1px solid #ddd">Total Tax</td>
          <td colspan="2" style="padding:7px 10px;font-size:11px;font-weight:700;text-align:right;border:1px solid #ddd">${fmtDec(totalTax)}</td>
        </tr>
        <tr style="background:#0e2240">
          <td colspan="5" style="padding:10px 12px;color:#fff;font-size:13px;font-weight:800">GRAND TOTAL</td>
          <td colspan="2" style="padding:10px 12px;color:#C9A84C;font-size:15px;font-weight:800;text-align:right">${cur} ${fmtDec(grandTotal)}</td>
        </tr>
      </tfoot>
    </table>

    <div style="margin:10px 0 16px;font-size:10px;color:#64748B;font-style:italic">We say the sum of <strong style="color:#0e2240">${words}</strong>.</div>

    ${paymentRows ? `
    <div style="margin-bottom:20px">
      <div style="font-size:11px;font-weight:700;color:#0e2240;margin-bottom:8px;text-decoration:underline">Payment History</div>
      <table style="width:100%;border-collapse:collapse">
        <thead><tr style="background:#f1f5f9">
          <th style="padding:6px 10px;font-size:10px;font-weight:700;text-align:left;border:1px solid #ddd">Ref</th>
          <th style="padding:6px 10px;font-size:10px;font-weight:700;text-align:left;border:1px solid #ddd">Date</th>
          <th style="padding:6px 10px;font-size:10px;font-weight:700;text-align:right;border:1px solid #ddd">Requested</th>
          <th style="padding:6px 10px;font-size:10px;font-weight:700;text-align:right;border:1px solid #ddd">Paid</th>
          <th style="padding:6px 10px;font-size:10px;font-weight:700;text-align:center;border:1px solid #ddd">Status</th>
        </tr></thead>
        <tbody>${paymentRows}</tbody>
      </table>
      <div style="margin-top:10px;display:flex;justify-content:flex-end;gap:24px;font-size:11px">
        <span>Total Paid: <strong style="color:#1A7A4A">${cur} ${fmtDec(totalPaid)}</strong></span>
        <span>Outstanding: <strong style="color:${outstanding > 0 ? '#E07B00' : '#1A7A4A'}">${cur} ${fmtDec(outstanding)}</strong></span>
      </div>
    </div>` : ''}

    ${buildFooter(property)}
    ${buildSignature(property)}`;
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* MAIN COMPONENT                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */
export default function CorporateSessions({ account, property, onBack }) {
  const [sessions,      setSessions]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [toast,         setToast]         = useState('');
  const [saving,        setSaving]        = useState(false);
  const [selected,      setSelected]      = useState(null);
  const [detail,        setDetail]        = useState(null);
  const [detailLoad,    setDetailLoad]    = useState(false);
  const [expandedGuest, setExpandedGuest] = useState(null);
  const [expandedReq,   setExpandedReq]   = useState(null);
  const [modal,         setModal]         = useState(null);
  const [roomTypes,      setRoomTypes]      = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [roomsLoading,   setRoomsLoading]   = useState(false);
  const [companyGuests, setCompanyGuests] = useState([]);   // PMS guests linked to this company
  const [cgLoading,     setCgLoading]     = useState(false);
  const [pickedGuests,  setPickedGuests]  = useState(new Set()); // selected in picker

  const currency = account?.currency || property?.currency || 'RWF';

  // ── Form state ─────────────────────────────────────────────────────────────
  const [sessionForm, setSessionForm] = useState(emptySession);
  const [tariffForm,  setTariffForm]  = useState(emptyTariff);
  const [guestForm,   setGuestForm]   = useState(emptyGuest);
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [requestForm, setRequestForm] = useState(emptyRequest);
  const [installForm, setInstallForm] = useState(emptyInstall);
  const [createResForm, setCreateResForm] = useState({ roomTypeId: '', roomId: '' });

  // ── Modal targets ──────────────────────────────────────────────────────────
  const [editSession,   setEditSession]   = useState(null);
  const [editTariff,    setEditTariff]    = useState(null);
  const [editGuest,     setEditGuest]     = useState(null);
  // Conference schedule (daily package + participants)
  const [dayForm,  setDayForm]  = useState({ id: '', serviceDate: '', tariffId: '', participants: '', notes: '' });
  const [bulkForm, setBulkForm] = useState({ tariffId: '', fromDate: '', toDate: '', participants: '' });
  const [serviceTarget, setServiceTarget] = useState(null);
  const [editService,   setEditService]   = useState(null);
  const [editRequest,   setEditRequest]   = useState(null);
  const [installTarget, setInstallTarget] = useState(null);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  // ── Empty form factories ───────────────────────────────────────────────────
  function emptySession() { return { sessionName: '', description: '', startDate: '', endDate: '', currency, paymentTerms: '{"type":"percentage","phases":[{"label":"Before Arrival","pct":50},{"label":"After Check-In","pct":50}]}', termsAndConditions: '', notes: '', status: 'pending', participantCount: '' }; }
  function emptyTariff()  { return { id: '', serviceType: 'accommodation', serviceLabel: '', unit: 'per night', negotiatedPrice: '', currency, notes: '', packageName: '', packageInclusives: '' }; }
  function emptyGuest()   { return { displayName: '', email: '', phone: '', department: '', jobTitle: '', notes: '', guestId: '', idNumber: '', stayStartDate: '', stayEndDate: '' }; }
  function emptyService() { return { serviceType: 'accommodation', serviceLabel: '', unit: 'per night', quantity: '1', unitPrice: '', notes: '' }; }
  function emptyRequest() { return { requestedAmount: '', currency, dueDate: '', paymentInstructions: property?.invoiceFooterText || '', notes: '' }; }
  function emptyInstall() { return { amount: '', currency, paymentMethod: 'bank', reference: '', notes: '' }; }

  // ── Load sessions scoped to THIS account ──────────────────────────────────
  const loadSessions = useCallback(async () => {
    if (!account?.id || !property?.id) return;
    setLoading(true);
    try {
      const data = await fetchCorporateSessions(property.id, null, account.id);
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) { showToast('Failed to load sessions: ' + e.message); }
    finally { setLoading(false); }
  }, [account?.id, property?.id]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Load room types once
  useEffect(() => {
    if (!property?.id) return;
    fetchRoomTypes(property.id).then(rt => setRoomTypes(Array.isArray(rt) ? rt : [])).catch(() => {});
  }, [property?.id]);

  // Load rooms when room type is selected inside createOneRes modal
  useEffect(() => {
    const roomTypeId = createResForm.roomTypeId;
    if (!selected?.id || !roomTypeId) { setAvailableRooms([]); return; }
    setRoomsLoading(true);
    setCreateResForm(f => ({ ...f, roomId: '' }));
    fetchAvailableRoomsForSession(selected.id, roomTypeId)
      .then(rooms => setAvailableRooms(Array.isArray(rooms) ? rooms : []))
      .catch(() => setAvailableRooms([]))
      .finally(() => setRoomsLoading(false));
  }, [createResForm.roomTypeId, selected?.id]);

  const loadDetail = useCallback(async id => {
    if (!id) return;
    setDetailLoad(true);
    try {
      const d = await fetchCorporateSessionDetail(id);
      setDetail(d);
      // FIX: every mutating action (recording a payment, adding/removing a
      // tariff or guest, issuing a payment request, etc.) already calls
      // loadDetail(selected.id) right after it succeeds — and this refreshes
      // `detail` correctly (installments, tariffs, guests all update live).
      // But the session HEADER/SUMMARY card (totalPaid, totalInvoiced,
      // totalEstimated, status) is drawn from a separate `selected` state
      // that was only ever set once, in openSession(). None of the action
      // handlers touched it, so those aggregate numbers stayed frozen at
      // whatever they were when the session was first opened, until the
      // whole page was reloaded. The detail response already includes the
      // session's own fresh copy under `session` — sync `selected` from it
      // here so every action refreshes the summary too, automatically.
      if (d?.session) setSelected(prev => (prev && prev.id === d.session.id ? d.session : prev));
    }
    catch (e) { showToast('Failed to load session detail: ' + e.message); }
    finally { setDetailLoad(false); }
  }, []);

  const openSession = s => {
    setSelected(s); setDetail(null); setExpandedGuest(null); setExpandedReq(null);
    loadDetail(s.id);
  };

  // ── Session CRUD ───────────────────────────────────────────────────────────
  const saveSession = async () => {
    if (!sessionForm.sessionName || !sessionForm.startDate || !sessionForm.endDate)
      return showToast('Session name, start and end dates are required.');
    setSaving(true);
    try {
      const payload = {
        ...sessionForm,
        propertyId: property?.id, corporateAccountId: account.id, companyName: account.name,
        // Conference-only headcount (Feature: conference/event without
        // accommodation). Send as a real number or null — never "" — since
        // the backend field is a plain Integer.
        participantCount: sessionForm.participantCount === '' || sessionForm.participantCount == null
          ? null : Number(sessionForm.participantCount),
      };
      if (editSession) {
        const u = await updateCorporateSession(editSession.id, payload);
        setSessions(s => s.map(x => x.id === u.id ? u : x));
        if (selected?.id === u.id) setSelected(u);
      } else {
        const c = await createCorporateSession(payload);
        setSessions(s => [c, ...s]);
      }
      showToast(editSession ? '✅ Session updated.' : '✅ Session created.');
      setModal(null);
      loadSessions();
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  // ── Tariff CRUD ────────────────────────────────────────────────────────────
  const saveTariff = async () => {
    if (!tariffForm.serviceType || !tariffForm.negotiatedPrice) return showToast('Service type and price required.');
    setSaving(true);
    try {
      const meta = svcMeta(tariffForm.serviceType);
      await saveSessionTariff(selected.id, {
        id: editTariff?.id || undefined,
        serviceType: tariffForm.serviceType,
        serviceLabel: tariffForm.serviceLabel || meta.label,
        unit: tariffForm.unit || meta.unit,
        negotiatedPrice: parseFloat(tariffForm.negotiatedPrice),
        currency: tariffForm.currency || currency,
        notes: tariffForm.notes,
        // Phase 6 & 7: Package/inclusive configuration
        packageName:       tariffForm.packageName?.trim()       || null,
        packageInclusives: tariffForm.packageInclusives?.trim() || null,
      });
      showToast('✅ Tariff saved.');
      setModal(null);
      loadDetail(selected.id);
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  const removeTariff = async id => {
    if (!window.confirm('Remove this tariff?')) return;
    try { await deleteSessionTariff(id); loadDetail(selected.id); showToast('Tariff removed.'); }
    catch (e) { showToast('❌ ' + e.message); }
  };

  // ── Conference schedule (one package on one date + that day's participants) ─
  const saveDay = async () => {
    if (!dayForm.serviceDate) return showToast('Choose the conference date.');
    if (!dayForm.tariffId)    return showToast('Choose the conference package.');
    setSaving(true);
    try {
      await saveConferenceDay(selected.id, {
        id: dayForm.id || undefined, serviceDate: dayForm.serviceDate, tariffId: dayForm.tariffId,
        participants: dayForm.participants === '' ? undefined : Number(dayForm.participants),
        notes: dayForm.notes || undefined,
      });
      showToast('✅ Conference day saved.'); setModal(null); loadDetail(selected.id);
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };
  const removeDay = async d => {
    if (!window.confirm('Remove this conference day?')) return;
    try { await deleteConferenceDay(selected.id, d.id); loadDetail(selected.id); showToast('Conference day removed.'); }
    catch (e) { showToast('❌ ' + e.message); }
  };
  const fillDays = async () => {
    if (!bulkForm.tariffId) return showToast('Choose the conference package.');
    setSaving(true);
    try {
      const r = await bulkConferenceDays(selected.id, {
        tariffId: bulkForm.tariffId, fromDate: bulkForm.fromDate || undefined, toDate: bulkForm.toDate || undefined,
        participants: bulkForm.participants === '' ? undefined : Number(bulkForm.participants),
      });
      showToast(`✅ ${r?.created ?? 0} day(s) added${r?.skipped ? `, ${r.skipped} already scheduled` : ''}.`);
      setModal(null); loadDetail(selected.id);
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  // ── Guest management ───────────────────────────────────────────────────────
  const openGuestPicker = async () => {
    setModal('guestPicker');
    setPickedGuests(new Set());
    setCgLoading(true);
    try {
      const gs = await fetchCorporateAccountGuests(account.id);
      // Filter out already enrolled guests
      const enrolledGuestIds = new Set(
        (detail?.guests || []).map(gd => gd.guest?.guestId).filter(Boolean)
      );
      setCompanyGuests((Array.isArray(gs) ? gs : []).filter(g => !enrolledGuestIds.has(g.id)));
    } catch (e) { showToast('Failed to load company guests: ' + e.message); setCompanyGuests([]); }
    finally { setCgLoading(false); }
  };

  const togglePick = id => setPickedGuests(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const bulkEnrol = async () => {
    if (pickedGuests.size === 0) return showToast('Select at least one guest.');
    setSaving(true);
    try {
      const res = await bulkEnrolSessionGuests(selected.id, [...pickedGuests]);
      showToast(`✅ ${res.enrolled} guest(s) enrolled${res.skipped > 0 ? `, ${res.skipped} already enrolled` : ''}.`);
      setModal(null);
      loadDetail(selected.id);
      loadSessions();
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  const saveGuest = async () => {
    if (!guestForm.displayName) return showToast('Guest name is required.');
    setSaving(true);
    try {
      if (editGuest) {
        // Update existing session guest record (including idNumber)
        await updateSessionGuest(editGuest.id, {
          displayName: guestForm.displayName, email: guestForm.email,
          phone: guestForm.phone, department: guestForm.department,
          jobTitle: guestForm.jobTitle, notes: guestForm.notes,
          idNumber: guestForm.idNumber,
          stayStartDate: guestForm.stayStartDate || undefined,
          stayEndDate:   guestForm.stayEndDate   || undefined,
        });
        showToast('✅ Guest updated.');
      } else {
        // ── Phase 5 fix: create global Guest record first, then enrol ─────
        // This ensures the guest appears in Guest Management and can be
        // linked to reservations. Also maps them to the corporate account.
        let resolvedGuestId = guestForm.guestId || null;
        if (!resolvedGuestId) {
          const nameParts = guestForm.displayName.trim().split(/\s+/);
          const globalGuest = await createGuest({
            firstName:          nameParts[0] || guestForm.displayName,
            lastName:           nameParts.slice(1).join(' ') || '',
            name:               guestForm.displayName,
            email:              guestForm.email || undefined,
            phone:              guestForm.phone || undefined,
            idNumber:           guestForm.idNumber || undefined,
            propertyId:         selected?.propertyId,
            corporateAccountId: selected?.corporateAccountId || undefined,
          });
          resolvedGuestId = globalGuest?.id || null;
        }
        // Enrol in the session with the resolved global guest ID
        await addSessionGuest(selected.id, {
          displayName: guestForm.displayName, email: guestForm.email,
          phone: guestForm.phone, department: guestForm.department,
          jobTitle: guestForm.jobTitle, notes: guestForm.notes,
          idNumber: guestForm.idNumber,
          stayStartDate: guestForm.stayStartDate || undefined,
          stayEndDate:   guestForm.stayEndDate   || undefined,
          guestId: resolvedGuestId || undefined,
        });
        showToast('✅ Guest enrolled and registered in guest database.');
      }
      setModal(null);
      loadDetail(selected.id);
      loadSessions();
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  const deleteGuest = async g => {
    if (!window.confirm(`Remove ${g.displayName}? Their planned services will also be deleted.`)) return;
    try { await removeSessionGuest(g.id); loadDetail(selected.id); loadSessions(); showToast('Guest removed.'); }
    catch (e) { showToast('❌ ' + e.message); }
  };

  // ── Planned Services ───────────────────────────────────────────────────────
  const prefillFromTariff = (type, tariffs) => {
    const match = (tariffs || []).find(t => t.serviceType === type);
    const meta  = svcMeta(type);
    setServiceForm(f => ({ ...f, serviceType: type, serviceLabel: match?.serviceLabel || meta.label, unit: match?.unit || meta.unit, unitPrice: match ? String(match.negotiatedPrice) : f.unitPrice }));
  };

  const saveService = async () => {
    if (!serviceForm.quantity || !serviceForm.unitPrice) return showToast('Quantity and price are required.');
    setSaving(true);
    try {
      const meta = svcMeta(serviceForm.serviceType);
      await savePlannedService(serviceTarget.id, {
        id: editService?.id || undefined,
        serviceType: serviceForm.serviceType, serviceLabel: serviceForm.serviceLabel || meta.label,
        unit: serviceForm.unit || meta.unit, quantity: parseFloat(serviceForm.quantity),
        unitPrice: parseFloat(serviceForm.unitPrice), notes: serviceForm.notes,
      });
      showToast('✅ Service saved.');
      setModal(null);
      loadDetail(selected.id);
      loadSessions();
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  const removeService = async id => {
    if (!window.confirm('Remove this planned service?')) return;
    try { await deletePlannedService(id); loadDetail(selected.id); loadSessions(); showToast('Service removed.'); }
    catch (e) { showToast('❌ ' + e.message); }
  };

  // ── Reservation creation & confirmation ────────────────────────────────────
  const handleCreateReservations = async (specificGuestId) => {
    if (specificGuestId && createResForm.roomTypeId && availableRooms.length > 0 && !createResForm.roomId) {
      return showToast('⚠️ Please select a specific room before creating the reservation.');
    }
    setSaving(true);
    try {
      const payload = { roomTypeId: createResForm.roomTypeId || undefined };
      if (specificGuestId) {
        payload.guestIds = [specificGuestId];
        if (createResForm.roomId) payload.roomId = createResForm.roomId;
      }
      const res = await createSessionReservations(selected.id, payload);
      showToast(`✅ ${res.created} reservation(s) created.${res.errors?.length ? ' ⚠️ Some failed.' : ''}`);
      setModal(null);
      setAvailableRooms([]);
      loadDetail(selected.id);
      loadSessions();
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  const handleConfirmAll = async () => {
    if (!window.confirm('Confirm all tentative reservations in this session? This allows check-in to proceed.')) return;
    setSaving(true);
    try {
      const res = await confirmSessionReservations(selected.id);
      showToast(`✅ ${res.confirmed} reservation(s) confirmed.`);
      loadDetail(selected.id);
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  // Bulk check-in: check in all confirmed reservations in this session
  const handleBulkCheckIn = async () => {
    const confirmed = (detail?.guests || []).filter(gd => gd.reservation?.status === 'confirmed');
    if (!confirmed.length) return showToast('No confirmed reservations to check in.');
    if (!window.confirm(`Check in ${confirmed.length} guest(s) now?`)) return;
    setSaving(true);
    let ok = 0, fail = 0;
    for (const gd of confirmed) {
      try { await checkIn(gd.reservation.id); ok++; }
      catch { fail++; }
    }
    showToast(`✅ ${ok} checked in${fail ? `, ⚠️ ${fail} failed` : ''}.`);
    loadDetail(selected.id);
    setSaving(false);
  };

  // Bulk check-out: check out all in-house guests in this session
  // skipBalanceCheck=true because corporate/contract companies pay on invoice terms,
  // not necessarily before checkout. Room is still marked dirty on the backend.
  const handleBulkCheckOut = async () => {
    const inHouse = (detail?.guests || []).filter(gd => gd.reservation?.status === 'checked_in');
    if (!inHouse.length) return showToast('No checked-in guests to check out.');
    if (!window.confirm(`Check out ${inHouse.length} guest(s) now?`)) return;
    setSaving(true);
    let ok = 0; const errors = [];
    for (const gd of inHouse) {
      try { await checkOut(gd.reservation.id, true); ok++; }
      catch (e) { errors.push(`${gd.displayName}: ${e.message}`); }
    }
    if (errors.length) showToast(`✅ ${ok} checked out. ⚠️ ${errors.length} failed: ${errors[0]}`);
    else showToast(`✅ ${ok} guest(s) checked out — rooms marked for housekeeping.`);
    loadDetail(selected.id);
    setSaving(false);
  };

  // ── Payment requests ───────────────────────────────────────────────────────
  const saveRequest = async () => {
    if (!requestForm.requestedAmount) return showToast('Amount is required.');
    setSaving(true);
    try {
      const payload = { ...requestForm, requestedAmount: parseFloat(requestForm.requestedAmount) };
      if (editRequest) await updateSessionPaymentRequest(editRequest.id, payload);
      else await createSessionPaymentRequest(selected.id, payload);
      showToast(editRequest ? '✅ Request updated.' : '✅ Payment request issued.');
      setModal(null);
      loadDetail(selected.id);
      loadSessions();
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  // ── Installments ───────────────────────────────────────────────────────────
  const saveInstall = async () => {
    if (!installForm.amount) return showToast('Amount is required.');
    setSaving(true);
    try {
      await recordInstallment(installTarget.id, { ...installForm, amount: parseFloat(installForm.amount) });
      showToast('✅ Payment recorded.');
      setModal(null);
      loadDetail(selected.id);
      loadSessions();
    } catch (e) { showToast('❌ ' + e.message); }
    finally { setSaving(false); }
  };

  // ── Print ──────────────────────────────────────────────────────────────────
  const printIndividual = async guest => {
    try {
      const data = await fetchIndividualProformaData(guest.id);
      openPrintWindow({ title: `Proforma — ${guest.displayName}`, format: 'letter', bodyHtml: buildIndividualProformaHtml({ ...data, property }) });
    } catch (e) { showToast('Print error: ' + e.message); }
  };

  const printCorporate = async () => {
    try {
      const data = await fetchCorporateProformaData(selected.id);
      openPrintWindow({ title: `Corporate Proforma — ${selected.companyName}`, format: 'letter', bodyHtml: buildCorporateProformaHtml({ ...data, property }) });
    } catch (e) { showToast('Print error: ' + e.message); }
  };

  const printPaymentReq = async req => {
    try {
      const installs = await fetchInstallments(req.id);
      openPrintWindow({ title: `Payment Request — ${req.requestNumber}`, format: 'letter', bodyHtml: buildPaymentRequestHtml({ session: selected, paymentRequest: req, installments: installs, guests: detail?.guests, property }) });
    } catch (e) { showToast('Print error: ' + e.message); }
  };

  const printFinalInvoice = async () => {
    try {
      const data = await fetchCorporateSessionInvoiceData(selected.id);
      openPrintWindow({ title: `Corporate Invoice — ${selected.companyName}`, format: 'letter', bodyHtml: buildFinalInvoiceHtml({ ...data, property }) });
    } catch (e) { showToast('Print error: ' + e.message); }
  };

  /* ═══════════════════════════════════════════════════════════════════════════
   * SESSION DETAIL VIEW
   * ════════════════════════════════════════════════════════════════════════ */
  if (selected) {
    const sess         = selected;
    const guestDetails = detail?.guests         || [];
    const tariffs      = detail?.tariffs        || [];
    const reqDetails   = detail?.paymentRequests || [];
    const cur          = sess.currency || currency;
    const estimated    = Number(sess.totalEstimated || 0);
    const paid         = Number(sess.totalPaid || 0);
    const pct          = estimated > 0 ? Math.min(100, Math.round((paid / estimated) * 100)) : 0;

    // Check if payment threshold met to enable "Confirm Reservations"
    const requiredPct = (() => {
      try { const m = sess.paymentTerms?.match(/"pct"\s*:\s*([0-9.]+)/); return m ? parseFloat(m[1]) : 50; } catch { return 50; }
    })();
    const requiredAmt  = estimated * requiredPct / 100;
    const paymentMet   = paid >= requiredAmt;

    // Count guests with/without reservations
    const pendingGuests  = guestDetails.filter(gd => !gd.guest.reservationId).length;
    const reservedGuests = guestDetails.filter(gd =>  gd.guest.reservationId).length;
    const tentativeReservations = guestDetails.filter(gd => gd.reservation?.status === 'tentative').length;

    return (
      <div className="fade-up">
        {toast && <Toast msg={toast} />}

        {/* Breadcrumb & actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.teal, cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: 0 }}>← {account.name}</button>
          <span style={{ color: T.muted }}>›</span>
          <button onClick={() => { setSelected(null); setDetail(null); }} style={{ background: 'none', border: 'none', color: T.teal, cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: 0 }}>Sessions</button>
          <span style={{ color: T.muted }}>›</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{sess.sessionName}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <SmBtn onClick={printCorporate} color={T.teal}>📄 Proforma</SmBtn>
            <SmBtn onClick={printFinalInvoice} color={T.navy} bg={T.surface} border={T.border}>🧾 Final Invoice</SmBtn>
            <SmBtn onClick={() => { setEditRequest(null); setRequestForm({ ...emptyRequest(), requestedAmount: String(Math.max(0, estimated - Number(sess.totalInvoiced || 0))) }); setModal('request'); }} color={T.white} bg={T.navy} border={T.navy}>+ Payment Request</SmBtn>
            <SmBtn onClick={() => { setEditSession(sess); setSessionForm({ sessionName: sess.sessionName, description: sess.description || '', startDate: sess.startDate, endDate: sess.endDate, currency: sess.currency || currency, paymentTerms: sess.paymentTerms || '', termsAndConditions: sess.termsAndConditions || '', notes: sess.notes || '', status: sess.status, participantCount: sess.participantCount ?? '' }); setModal('session'); }} color={T.muted} bg={T.surface} border={T.border}>✏️ Edit</SmBtn>
          </div>
        </div>

        {/* Session header card */}
        <div style={{ background: `linear-gradient(135deg,${T.navy},#1F3A52)`, borderRadius: 14, padding: '20px 24px', marginBottom: 20, boxShadow: SHADOW.md }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: '#C9A84C', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>{sess.companyName}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: T.white, marginBottom: 6 }}>{sess.sessionName}</div>
              <div style={{ fontSize: 12, color: '#94A3B8' }}>📅 {fmtDate(sess.startDate)} — {fmtDate(sess.endDate)}</div>
            </div>
            <SBadge status={sess.status} />
          </div>
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: .8 }}>Payment Progress ({requiredPct}% required)</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.white }}>{pct}%</span>
            </div>
            <div style={{ height: 6, background: 'rgba(255,255,255,.15)', borderRadius: 99 }}>
              <div style={{ height: 6, width: `${pct}%`, background: paymentMet ? T.success : '#C9A84C', borderRadius: 99, transition: 'width .4s' }} />
            </div>
          </div>
          {/* Stats row */}
          <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            {[
              { label: 'Estimated',   value: `${fmt(estimated)} ${cur}` },
              { label: 'Invoiced',    value: `${fmt(sess.totalInvoiced || 0)} ${cur}` },
              { label: 'Paid',        value: `${fmt(paid)} ${cur}` },
              { label: 'Outstanding', value: `${fmt(Math.max(0, estimated - paid))} ${cur}` },
              { label: 'Guests',      value: `${guestDetails.length} enrolled` },
            ].map(p => (
              <div key={p.label} style={{ background: 'rgba(255,255,255,.08)', borderRadius: 10, padding: '8px 14px', minWidth: 100 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: .7, marginBottom: 2 }}>{p.label}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.white }}>{p.value}</div>
              </div>
            ))}
          </div>
          {/* Bulk action CTAs — Phase 11: shown based on lifecycle status, not payment */}
          {tentativeReservations > 0 && (
            <div style={{ marginTop: 14, background: T.navy + 'cc', borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 12, color: T.white }}>📋 {tentativeReservations} reservation(s) are still tentative and need confirmation.</span>
              <SmBtn onClick={handleConfirmAll} color={T.white} bg={T.success} border={T.success} disabled={saving}>✓ Confirm All</SmBtn>
            </div>
          )}
          {(() => {
            const confirmedCount = (detail?.guests || []).filter(gd => gd.reservation?.status === 'confirmed').length;
            const inHouseCount   = (detail?.guests || []).filter(gd => gd.reservation?.status === 'checked_in').length;
            return (
              <>
                {confirmedCount > 0 && (
                  <div style={{ marginTop: 10, background: T.teal + '22', border: `1px solid ${T.teal}44`, borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 12, color: T.white }}>🏨 {confirmedCount} guest(s) confirmed and ready to check in.</span>
                    <SmBtn onClick={handleBulkCheckIn} color={T.white} bg={T.teal} border={T.teal} disabled={saving}>🛎 Bulk Check-In</SmBtn>
                  </div>
                )}
                {inHouseCount > 0 && (
                  <div style={{ marginTop: 10, background: '#7C3AED22', border: '1px solid #7C3AED44', borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 12, color: T.white }}>🚪 {inHouseCount} guest(s) currently in-house.</span>
                    <SmBtn onClick={handleBulkCheckOut} color={T.white} bg="#7C3AED" border="#7C3AED" disabled={saving}>🚪 Bulk Check-Out</SmBtn>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {detailLoad && <div style={{ textAlign: 'center', padding: 40, color: T.muted, fontSize: 12 }}>Loading session details…</div>}

        {!detailLoad && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

            {/* LEFT: Tariffs + Payment Requests */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Tariffs */}
              <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: SHADOW.sm }}>
                <SectionHead icon="💰" title="Session Tariffs"
                  action={<SmBtn onClick={() => { setEditTariff(null); setTariffForm(emptyTariff()); setModal('tariff'); }}>+ Add</SmBtn>} />
                {tariffs.length === 0 && <p style={{ fontSize: 12, color: T.muted, textAlign: 'center', padding: '16px 0' }}>No tariffs yet. Add agreed prices for this session.</p>}
                {tariffs.map(t => {
                  let inclusives = [];
                  if (t.packageInclusives) {
                    try { inclusives = JSON.parse(t.packageInclusives); }
                    catch { inclusives = t.packageInclusives.split(',').map(s => s.trim()).filter(Boolean); }
                  }
                  return (
                    <div key={t.id} style={{ padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: T.navy }}>{svcMeta(t.serviceType).icon} {t.serviceLabel || t.serviceType}</span>
                          <span style={{ fontSize: 10, color: T.muted, marginLeft: 6 }}>{t.unit}</span>
                          {t.packageName && (
                            <span style={{ fontSize: 10, color: '#1A7A4A', fontWeight: 600, marginLeft: 6 }}>↳ {t.packageName}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: T.teal }}>{fmt(t.negotiatedPrice)} {t.currency || cur}</span>
                          <button onClick={() => {
                            setEditTariff(t);
                            setTariffForm({
                              id: t.id, serviceType: t.serviceType, serviceLabel: t.serviceLabel || '',
                              unit: t.unit || '', negotiatedPrice: String(t.negotiatedPrice),
                              currency: t.currency || cur, notes: t.notes || '',
                              packageName: t.packageName || '',
                              packageInclusives: inclusives.length ? inclusives.join(', ') : (t.packageInclusives || ''),
                            });
                            setModal('tariff');
                          }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                          <button onClick={() => removeTariff(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: T.danger }}>×</button>
                        </div>
                      </div>
                      {inclusives.length > 0 && (
                        <div style={{ marginTop: 4, paddingLeft: 20, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {inclusives.map((item, i) => (
                            <span key={i} style={{ fontSize: 9, background: '#F0F9FF', border: '1px solid #BAE6FD', color: '#0369A1', borderRadius: 99, padding: '1px 7px' }}>{item}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Participants — total vs individually registered (related, NOT additive) */}
              {(() => {
                const ps = detail?.participantSummary;
                if (!ps) return null;
                const cell = (label, value, color) => (
                  <div style={{ flex: 1, textAlign: 'center', padding: '8px 4px', background: T.surface, borderRadius: 10 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
                    <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{label}</div>
                  </div>
                );
                return (
                  <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: SHADOW.sm }}>
                    <SectionHead icon="👥" title="Participants"
                      action={<SmBtn onClick={() => { setEditSession(sess); setSessionForm({ sessionName: sess.sessionName, description: sess.description || '', startDate: sess.startDate, endDate: sess.endDate, currency: sess.currency || currency, paymentTerms: sess.paymentTerms || '', termsAndConditions: sess.termsAndConditions || '', notes: sess.notes || '', status: sess.status, participantCount: sess.participantCount ?? '' }); setModal('session'); }}>✏️ Set total</SmBtn>} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      {cell('Total participants', ps.totalParticipants, T.navy)}
                      {cell('Registered individually', ps.registeredParticipants, T.teal)}
                      {cell('Not registered individually', ps.notRegistered, T.muted)}
                    </div>
                    <p style={{ fontSize: 10, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
                      Registered guests are <strong>part of</strong> the total, not in addition to it
                      ({ps.totalParticipants} = {ps.registeredParticipants} registered + {ps.notRegistered} others).
                      Only registered guests need names, rooms and personal services.
                    </p>
                    {ps.warning && <p style={{ fontSize: 11, color: T.danger, marginTop: 6 }}>⚠️ {ps.warning}</p>}
                  </div>
                );
              })()}

              {/* Conference Schedule — one package per date, with that day's participants */}
              {(() => {
                const days     = detail?.conferenceDays || [];
                const confTar  = tariffs.filter(t => isConfType(t.serviceType));
                const daysTotal = days.reduce((a, d) => a + Number(d.amount || 0), 0);
                return (
                  <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: SHADOW.sm }}>
                    <SectionHead icon="🗓️" title="Conference Schedule"
                      action={confTar.length > 0 && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <SmBtn onClick={() => { setBulkForm({ tariffId: confTar[0].id, fromDate: sess.startDate || '', toDate: sess.endDate || '', participants: '' }); setModal('confbulk'); }}>⚡ Fill days</SmBtn>
                          <SmBtn onClick={() => { setDayForm({ id: '', serviceDate: sess.startDate || '', tariffId: confTar[0].id, participants: '', notes: '' }); setModal('confday'); }}>+ Day</SmBtn>
                        </div>
                      )} />
                    {confTar.length === 0 && (
                      <p style={{ fontSize: 12, color: T.muted, textAlign: 'center', padding: '12px 0' }}>
                        Add a conference package under <strong>Session Tariffs</strong> first (e.g. Full Day Conference), then schedule it per day here.
                      </p>
                    )}
                    {confTar.length > 0 && days.length === 0 && (
                      <p style={{ fontSize: 12, color: T.muted, textAlign: 'center', padding: '12px 0' }}>
                        No days scheduled. Use <strong>Fill days</strong> to apply a package to every date, then change individual days (e.g. Half Day on the last day).
                      </p>
                    )}
                    {days.map(d => (
                      <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.navy }}>
                            {new Date(d.serviceDate + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            <span style={{ fontWeight: 600, color: T.teal, marginLeft: 8 }}>{d.serviceLabel || d.serviceType}</span>
                          </div>
                          <div style={{ fontSize: 10, color: T.muted }}>{d.participants} participants × {fmt(d.unitPrice)} {cur}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: T.teal }}>{fmt(d.amount)}</span>
                          <button onClick={() => { setDayForm({ id: d.id, serviceDate: d.serviceDate, tariffId: d.tariffId || '', participants: String(d.participants), notes: d.notes || '' }); setModal('confday'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                          <button onClick={() => removeDay(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: T.danger }}>×</button>
                        </div>
                      </div>
                    ))}
                    {days.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, fontSize: 12, fontWeight: 800, color: T.navy }}>
                        <span>Conference total (VAT inclusive)</span><span>{fmt(daysTotal)} {cur}</span>
                      </div>
                    )}
                    {days.length > 0 && (
                      <p style={{ fontSize: 10, color: T.muted, marginTop: 6, lineHeight: 1.5 }}>
                        The schedule bills the conference package for <strong>all</strong> participants, registered guests included.
                        Do not add the conference package to individual guests — add only their accommodation and other services.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Payment Requests */}
              <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: SHADOW.sm }}>
                <SectionHead icon="💳" title="Payment Requests"
                  action={<SmBtn onClick={() => { setEditRequest(null); setRequestForm({ ...emptyRequest(), requestedAmount: String(Math.max(0, estimated - Number(sess.totalInvoiced || 0))) }); setModal('request'); }}>+ New</SmBtn>} />
                {reqDetails.length === 0 && <p style={{ fontSize: 12, color: T.muted, textAlign: 'center', padding: '16px 0' }}>No payment requests yet.</p>}
                {reqDetails.map(rd => {
                  const req   = rd.request || rd;
                  const insts = rd.installments || [];
                  const reqPct = req.requestedAmount > 0 ? Math.min(100, Math.round(((req.amountPaid || 0) / req.requestedAmount) * 100)) : 0;
                  const sc     = { pending: { color: T.warning, bg: T.warningPale, label: 'Pending' }, partially_paid: { color: T.teal, bg: T.tealPale, label: 'Partial' }, paid: { color: T.success, bg: T.successPale, label: 'Paid' }, overdue: { color: T.danger, bg: T.dangerPale, label: 'Overdue' }, cancelled: { color: T.muted, bg: T.surface, label: 'Cancelled' } }[req.status] || { color: T.muted, bg: T.surface, label: req.status };
                  const open   = expandedReq === req.id;
                  return (
                    <div key={req.id} style={{ marginBottom: 12, padding: '12px 14px', background: T.surface, borderRadius: 10, border: `1px solid ${T.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button onClick={() => setExpandedReq(open ? null : req.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: T.muted }}>{open ? '▾' : '▸'}</button>
                          <span style={{ fontSize: 12, fontWeight: 800, color: T.navy }}>{req.requestNumber}</span>
                          {req.dueDate && <span style={{ fontSize: 10, color: T.muted }}>Due: {fmtDate(req.dueDate)}</span>}
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 700, color: sc.color, background: sc.bg, padding: '2px 8px', borderRadius: 99, textTransform: 'uppercase' }}>{sc.label}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: T.muted }}>Requested</span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: T.navy }}>{fmt(req.requestedAmount)} {req.currency || cur}</span>
                      </div>
                      <div style={{ height: 4, background: T.border, borderRadius: 99, marginBottom: 8 }}>
                        <div style={{ height: 4, width: `${reqPct}%`, background: reqPct >= 100 ? T.success : T.teal, borderRadius: 99 }} />
                      </div>
                      {open && insts.map(i => (
                        <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.success, marginBottom: 2, paddingLeft: 16 }}>
                          <span>✓ {fmtDateTime(i.paidAt)} — {i.paymentMethod}{i.reference ? ` (${i.reference})` : ''}</span>
                          <span>{fmt(i.amount)} {i.currency || cur}</span>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                        <SmBtn onClick={() => printPaymentReq(req)} color={T.navy} bg={T.white} border={T.border}>🖨️ Print</SmBtn>
                        {req.status !== 'paid' && req.status !== 'cancelled' && (
                          <SmBtn onClick={() => { setInstallTarget(req); setInstallForm({ ...emptyInstall(), currency: req.currency || cur, amount: String(req.requestedAmount - (req.amountPaid || 0)) }); setModal('install'); }} color={T.success} bg={T.successPale} border={T.success}>+ Record Payment</SmBtn>
                        )}
                        <SmBtn onClick={() => { setEditRequest(req); setRequestForm({ requestedAmount: String(req.requestedAmount), currency: req.currency || cur, dueDate: req.dueDate || '', paymentInstructions: req.paymentInstructions || '', notes: req.notes || '' }); setModal('request'); }} color={T.muted} bg={T.surface} border={T.border}>✏️</SmBtn>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: Guests */}
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: SHADOW.sm }}>
              <SectionHead icon="👥" title={`Enrolled Guests (${guestDetails.length})`}
                action={
                  <div style={{ display: 'flex', gap: 6 }}>
                    <SmBtn onClick={openGuestPicker} color={T.teal}>👥 From Company</SmBtn>
                    <SmBtn onClick={() => { setEditGuest(null); setGuestForm(emptyGuest()); setModal('guest'); }}>+ Manual</SmBtn>
                  </div>
                } />

              {/* Bulk create reservations banner */}
              {pendingGuests > 0 && (
                <div style={{ background: T.tealPale, borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 11, color: T.teal, fontWeight: 600 }}>
                    {pendingGuests} guest(s) have no reservation yet.
                  </span>
                  <SmBtn onClick={() => { setCreateResForm({ roomTypeId: '', roomId: '' }); setAvailableRooms([]); setModal('createRes'); }} color={T.teal} bg={T.teal + '20'} border={T.teal}>🏨 Create All Reservations</SmBtn>
                </div>
              )}

              {guestDetails.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <p style={{ fontSize: 32, marginBottom: 12 }}>👥</p>
                  <p style={{ fontSize: 13, color: T.muted }}>No guests enrolled yet.</p>
                  <p style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>Add company employees to this session using the buttons above.</p>
                </div>
              )}

              {guestDetails.map(gd => {
                const g    = gd.guest;
                const svcs = gd.services || [];
                const gTotal = Number(gd.total || 0);
                const open   = expandedGuest === g.id;
                const res    = gd.reservation;

                return (
                  <div key={g.id} style={{ marginBottom: 14, padding: '12px 14px', background: T.surface, borderRadius: 10, border: `1px solid ${T.border}` }}>
                    {/* Guest header row */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: open ? 10 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button onClick={() => setExpandedGuest(open ? null : g.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: T.muted, flexShrink: 0 }}>{open ? '▾' : '▸'}</button>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 800, color: T.navy }}>👤 {g.displayName}</span>
                            {g.department && <span style={{ fontSize: 10, color: T.muted }}>({g.department})</span>}
                            {g.idNumber && <span style={{ fontSize: 9, color: '#0369A1', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 99, padding: '1px 7px' }}>🪪 {g.idNumber}</span>}
                            <GuestStatusBadge status={g.status} />
                          </div>
                          {res && (
                            <div style={{ fontSize: 10, color: T.teal, marginTop: 2 }}>
                              🏨 {res.confirmationNumber}{res.roomNumber ? ` · Room ${res.roomNumber}` : ''}{res.roomTypeName ? ` · ${res.roomTypeName}` : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                        {gTotal > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: T.teal }}>{fmt(gTotal)} {cur}</span>}
                        <SmBtn onClick={() => printIndividual(g)} color={T.navy} bg={T.white} border={T.border}>📄</SmBtn>
                        {!g.reservationId && (
                          <SmBtn onClick={() => { setCreateResForm({ roomTypeId: '', roomId: '' }); setAvailableRooms([]); setModal('createOneRes'); setServiceTarget(g); }} color={T.teal} bg={T.tealPale} border={T.teal}>🏨</SmBtn>
                        )}
                        <SmBtn onClick={() => { setEditGuest(g); setGuestForm({ displayName: g.displayName, email: g.email || '', phone: g.phone || '', department: g.department || '', jobTitle: g.jobTitle || '', notes: g.notes || '', guestId: g.guestId || '', idNumber: g.idNumber || '', stayStartDate: g.stayStartDate || '', stayEndDate: g.stayEndDate || '' }); setModal('guest'); }} color={T.muted} bg={T.white} border={T.border}>✏️</SmBtn>
                        <SmBtn onClick={() => deleteGuest(g)} color={T.danger} bg={T.dangerPale} border={T.danger}>×</SmBtn>
                      </div>
                    </div>

                    {open && (
                      <>
                        {svcs.length > 0 && (
                          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                            <thead>
                              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                                {['Service', 'Qty', 'Price', 'Amount', ''].map(h => (
                                  <th key={h} style={{ fontSize: 9, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .6, textAlign: h === '' || h === 'Service' ? 'left' : 'right', padding: '4px 0', paddingRight: 6 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {svcs.map(s => (
                                <tr key={s.id}>
                                  <td style={{ fontSize: 11, color: T.navy, padding: '5px 0' }}>{svcMeta(s.serviceType).icon} {s.serviceLabel || s.serviceType}</td>
                                  <td style={{ fontSize: 11, textAlign: 'right', padding: '5px 6px 5px 0', color: T.muted }}>{fmt(s.quantity)}</td>
                                  <td style={{ fontSize: 11, textAlign: 'right', padding: '5px 6px 5px 0', color: T.muted }}>{fmt(s.unitPrice)}</td>
                                  <td style={{ fontSize: 11, fontWeight: 700, textAlign: 'right', color: T.navy, padding: '5px 6px 5px 0' }}>{fmt(s.plannedAmount)}</td>
                                  <td style={{ textAlign: 'right', padding: '5px 0', whiteSpace: 'nowrap' }}>
                                    <button onClick={() => { setServiceTarget(g); setEditService(s); setServiceForm({ serviceType: s.serviceType, serviceLabel: s.serviceLabel || '', unit: s.unit || '', quantity: String(s.quantity), unitPrice: String(s.unitPrice), notes: s.notes || '' }); setModal('service'); }} style={{ background: 'none', border: 'none', color: T.teal, cursor: 'pointer', fontSize: 11, marginRight: 4 }}>✏️</button>
                                    <button onClick={() => removeService(s.id)} style={{ background: 'none', border: 'none', color: T.danger, cursor: 'pointer', fontSize: 12 }}>×</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        <SmBtn onClick={() => { setServiceTarget(g); setEditService(null); const m = tariffs[0]; setServiceForm({ ...emptyService(), unitPrice: m ? String(m.negotiatedPrice) : '', unit: m?.unit || 'per night' }); setModal('service'); }} color={T.teal} bg={T.white} border={T.teal}>+ Add Service</SmBtn>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── MODALS ── */}

        {/* Session form */}
        {modal === 'session' && (
          <Modal title={editSession ? 'Edit Session' : 'New Session'} onClose={() => setModal(null)} wide>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Session Name" required><Inp value={sessionForm.sessionName} onChange={v => setSessionForm(f => ({ ...f, sessionName: v }))} placeholder="July Leadership Training" /></Fld>
              <Fld label="Status"><Sel value={sessionForm.status} onChange={v => setSessionForm(f => ({ ...f, status: v }))}>{Object.entries(SESSION_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</Sel></Fld>
            </div>
            <Fld label="Description"><Inp value={sessionForm.description} rows={2} onChange={v => setSessionForm(f => ({ ...f, description: v }))} /></Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Fld label="Start Date" required><Inp type="date" value={sessionForm.startDate} onChange={v => setSessionForm(f => ({ ...f, startDate: v }))} /></Fld>
              <Fld label="End Date" required><Inp type="date" value={sessionForm.endDate} onChange={v => setSessionForm(f => ({ ...f, endDate: v }))} /></Fld>
              <Fld label="Currency"><Inp value={sessionForm.currency} onChange={v => setSessionForm(f => ({ ...f, currency: v }))} /></Fld>
            </div>
            <Fld label="Total Conference Participants (optional)"
                 hint="Everyone attending — including the people you register individually below (they are part of this total, not extra). Only people who need rooms or personal services must be registered.">
              <Inp type="number" value={sessionForm.participantCount}
                   onChange={v => setSessionForm(f => ({ ...f, participantCount: v }))}
                   placeholder="e.g. 80" />
            </Fld>
            <Fld label="Terms & Conditions" hint="Printed on proforma invoices and payment requests."><Inp value={sessionForm.termsAndConditions} rows={4} onChange={v => setSessionForm(f => ({ ...f, termsAndConditions: v }))} /></Fld>
            <Fld label="Notes"><Inp value={sessionForm.notes} rows={2} onChange={v => setSessionForm(f => ({ ...f, notes: v }))} /></Fld>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <PrimaryBtn onClick={saveSession} loading={saving} full>{editSession ? 'Update Session' : 'Create Session'}</PrimaryBtn>
              <button onClick={() => setModal(null)} style={{ background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 9, padding: '11px 18px', cursor: 'pointer', fontFamily: FONTS.body }}>Cancel</button>
            </div>
          </Modal>
        )}

        {/* Conference day form */}
        {modal === 'confday' && (
          <Modal title={dayForm.id ? 'Edit Conference Day' : 'Add Conference Day'} onClose={() => setModal(null)}>
            <Fld label="Date" required><Inp type="date" value={dayForm.serviceDate} onChange={v => setDayForm(f => ({ ...f, serviceDate: v }))} /></Fld>
            <Fld label="Conference Package" required hint="Packages come from this session's agreed tariffs.">
              <Sel value={dayForm.tariffId} onChange={v => setDayForm(f => ({ ...f, tariffId: v }))}>
                {tariffs.filter(t => isConfType(t.serviceType)).map(t => (
                  <option key={t.id} value={t.id}>{t.serviceLabel || t.serviceType}{t.packageName ? ` — ${t.packageName}` : ''} ({fmt(t.negotiatedPrice)} {t.currency || cur})</option>
                ))}
              </Sel>
            </Fld>
            <Fld label="Participants that day" hint="Everyone attending, including people registered individually. Leave blank to use the session total.">
              <Inp type="number" value={dayForm.participants} onChange={v => setDayForm(f => ({ ...f, participants: v }))}
                   placeholder={detail?.participantSummary ? String(detail.participantSummary.totalParticipants) : ''} />
            </Fld>
            <Fld label="Notes"><Inp value={dayForm.notes} onChange={v => setDayForm(f => ({ ...f, notes: v }))} /></Fld>
            <PrimaryBtn onClick={saveDay} loading={saving} full variant="teal">Save Day</PrimaryBtn>
          </Modal>
        )}

        {/* Fill conference days */}
        {modal === 'confbulk' && (
          <Modal title="Fill Conference Days" onClose={() => setModal(null)}>
            <Fld label="Conference Package" required>
              <Sel value={bulkForm.tariffId} onChange={v => setBulkForm(f => ({ ...f, tariffId: v }))}>
                {tariffs.filter(t => isConfType(t.serviceType)).map(t => (
                  <option key={t.id} value={t.id}>{t.serviceLabel || t.serviceType}{t.packageName ? ` — ${t.packageName}` : ''}</option>
                ))}
              </Sel>
            </Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="From"><Inp type="date" value={bulkForm.fromDate} onChange={v => setBulkForm(f => ({ ...f, fromDate: v }))} /></Fld>
              <Fld label="To"><Inp type="date" value={bulkForm.toDate} onChange={v => setBulkForm(f => ({ ...f, toDate: v }))} /></Fld>
            </div>
            <Fld label="Participants per day" hint="Blank = session total. You can change individual days afterwards.">
              <Inp type="number" value={bulkForm.participants} onChange={v => setBulkForm(f => ({ ...f, participants: v }))} />
            </Fld>
            <PrimaryBtn onClick={fillDays} loading={saving} full variant="teal">Add Days</PrimaryBtn>
          </Modal>
        )}

        {/* Tariff form */}
        {modal === 'tariff' && (
          <Modal title={editTariff ? 'Edit Tariff' : 'Add Session Tariff'} onClose={() => setModal(null)}>
            <Fld label="Service Type" required>
              <Sel value={tariffForm.serviceType} onChange={v => { const m = svcMeta(v); setTariffForm(f => ({ ...f, serviceType: v, serviceLabel: m.label, unit: m.unit })); }}>
                {Object.entries(
                  SERVICE_TYPES.reduce((acc, s) => {
                    const g = s.group || 'Other';
                    if (!acc[g]) acc[g] = [];
                    acc[g].push(s);
                    return acc;
                  }, {})
                ).map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
                  </optgroup>
                ))}
              </Sel>
            </Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Unit"><Inp value={tariffForm.unit} onChange={v => setTariffForm(f => ({ ...f, unit: v }))} placeholder="per night" /></Fld>
              <Fld label="Agreed Price" required><Inp type="number" value={tariffForm.negotiatedPrice} onChange={v => setTariffForm(f => ({ ...f, negotiatedPrice: v }))} /></Fld>
            </div>
            <Fld label="Currency"><Inp value={tariffForm.currency} onChange={v => setTariffForm(f => ({ ...f, currency: v }))} /></Fld>

            {/* Phase 6 & 7 — Package configuration (conference service types) */}
            {['conference','conference_full_day','conference_half_day','conference_evening','conference_board_room'].includes(tariffForm.serviceType) && (
              <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: '#0369A1', marginBottom: 10 }}>📦 Package Configuration <span style={{ fontWeight: 400, fontSize: 10, color: T.muted }}>(optional — leave blank if not a package)</span></p>
                <Fld label="Package Name" hint="e.g. Full Day Conference, Half Day Conference, Bed & Breakfast">
                  <Inp value={tariffForm.packageName} onChange={v => setTariffForm(f => ({ ...f, packageName: v }))} placeholder="Full Day Conference" />
                </Fld>
                <Fld label="Inclusive Items" hint="Comma-separated list of what's included in this package price">
                  <Inp
                    rows={3}
                    value={tariffForm.packageInclusives}
                    onChange={v => setTariffForm(f => ({ ...f, packageInclusives: v }))}
                    placeholder="Lunch, Tea/Coffee Break (Morning & Afternoon), Meeting Room, Water, Flip Chart, Screen & PA System"
                  />
                </Fld>
                {tariffForm.packageInclusives && (
                  <div style={{ marginTop: 4 }}>
                    <p style={{ fontSize: 10, color: T.muted, marginBottom: 4 }}>Preview:</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {tariffForm.packageInclusives.split(',').map((item, i) => item.trim() && (
                        <span key={i} style={{ fontSize: 10, background: '#E0F2FE', color: '#0369A1', padding: '2px 8px', borderRadius: 99, border: '1px solid #BAE6FD' }}>
                          {item.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <Fld label="Notes"><Inp value={tariffForm.notes} onChange={v => setTariffForm(f => ({ ...f, notes: v }))} /></Fld>
            <PrimaryBtn onClick={saveTariff} loading={saving} full variant="teal">Save Tariff</PrimaryBtn>
          </Modal>
        )}

        {/* Guest picker (from company PMS guests) */}
        {modal === 'guestPicker' && (
          <Modal title={`Enrol from ${account.name} — Company Guests`} onClose={() => setModal(null)} wide>
            {cgLoading && <p style={{ textAlign: 'center', color: T.muted, fontSize: 12, padding: 20 }}>Loading company guests…</p>}
            {!cgLoading && companyGuests.length === 0 && (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <p style={{ fontSize: 13, color: T.muted }}>No more guests to enrol.</p>
                <p style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>Link guests to {account.name} via Guest Management, or use "Manual" to add ad-hoc attendees.</p>
              </div>
            )}
            {!cgLoading && companyGuests.length > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 11, color: T.muted }}>{companyGuests.length} guest(s) available · {pickedGuests.size} selected</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <SmBtn onClick={() => setPickedGuests(new Set(companyGuests.map(g => g.id)))} color={T.teal}>Select All</SmBtn>
                    <SmBtn onClick={() => setPickedGuests(new Set())} color={T.muted}>Clear</SmBtn>
                  </div>
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: 10 }}>
                  {companyGuests.map(g => (
                    <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', background: pickedGuests.has(g.id) ? T.tealPale : T.white }}>
                      <input type="checkbox" checked={pickedGuests.has(g.id)} onChange={() => togglePick(g.id)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{g.fullName || `${g.firstName} ${g.lastName}`}</p>
                        <p style={{ fontSize: 10, color: T.muted }}>{g.email || '—'}{g.phone ? ' · ' + g.phone : ''}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <PrimaryBtn onClick={bulkEnrol} loading={saving} disabled={pickedGuests.size === 0} full variant="teal">Enrol {pickedGuests.size > 0 ? pickedGuests.size + ' ' : ''}Selected Guest(s)</PrimaryBtn>
                  <button onClick={() => setModal(null)} style={{ background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 9, padding: '11px 18px', cursor: 'pointer', fontFamily: FONTS.body }}>Cancel</button>
                </div>
              </>
            )}
          </Modal>
        )}

        {/* Manual guest form */}
        {modal === 'guest' && (
          <Modal title={editGuest ? 'Edit Guest' : 'Enrol Guest (Manual)'} onClose={() => setModal(null)}>
            <Fld label="Full Name" required><Inp value={guestForm.displayName} onChange={v => setGuestForm(f => ({ ...f, displayName: v }))} placeholder="John Doe" /></Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Email"><Inp type="email" value={guestForm.email} onChange={v => setGuestForm(f => ({ ...f, email: v }))} /></Fld>
              <Fld label="Phone"><Inp value={guestForm.phone} onChange={v => setGuestForm(f => ({ ...f, phone: v }))} /></Fld>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Department"><Inp value={guestForm.department} onChange={v => setGuestForm(f => ({ ...f, department: v }))} placeholder="Finance, HR…" /></Fld>
              <Fld label="Job Title"><Inp value={guestForm.jobTitle} onChange={v => setGuestForm(f => ({ ...f, jobTitle: v }))} /></Fld>
            </div>
            {/* Phase 3 — Guest ID Number */}
            <Fld label="ID / Passport Number" hint="National ID, passport, or other document number">
              <Inp value={guestForm.idNumber} onChange={v => setGuestForm(f => ({ ...f, idNumber: v }))} placeholder="Document number (optional)" />
            </Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Stay From (optional)" hint="Only if this person stays on different dates than the whole session. Blank = session dates.">
                <Inp type="date" value={guestForm.stayStartDate} onChange={v => setGuestForm(f => ({ ...f, stayStartDate: v }))} />
              </Fld>
              <Fld label="Stay To (optional)" hint="Check-out date.">
                <Inp type="date" value={guestForm.stayEndDate} onChange={v => setGuestForm(f => ({ ...f, stayEndDate: v }))} />
              </Fld>
            </div>
            <Fld label="Notes"><Inp value={guestForm.notes} rows={2} onChange={v => setGuestForm(f => ({ ...f, notes: v }))} /></Fld>
            <PrimaryBtn onClick={saveGuest} loading={saving} full variant="teal">{editGuest ? 'Update Guest' : 'Enrol Guest'}</PrimaryBtn>
          </Modal>
        )}

        {/* Planned service form */}
        {modal === 'service' && serviceTarget && (
          <Modal title={`${editService ? 'Edit' : 'Add'} Service — ${serviceTarget.displayName}`} onClose={() => setModal(null)}>
            <Fld label="Service Type" required>
              <Sel value={serviceForm.serviceType} onChange={v => { setServiceForm(f => ({ ...f, serviceType: v })); prefillFromTariff(v, tariffs); }}>
                {Object.entries(
                  SERVICE_TYPES.reduce((acc, s) => {
                    const g = s.group || 'Other';
                    if (!acc[g]) acc[g] = [];
                    acc[g].push(s);
                    return acc;
                  }, {})
                ).map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
                  </optgroup>
                ))}
              </Sel>
            </Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Quantity" required><Inp type="number" value={serviceForm.quantity} onChange={v => setServiceForm(f => ({ ...f, quantity: v }))} /></Fld>
              <Fld label="Unit"><Inp value={serviceForm.unit} onChange={v => setServiceForm(f => ({ ...f, unit: v }))} /></Fld>
            </div>
            <Fld label="Unit Price" required>
              <Inp type="number" value={serviceForm.unitPrice} onChange={v => setServiceForm(f => ({ ...f, unitPrice: v }))} />
              {tariffs.find(t => t.serviceType === serviceForm.serviceType) && (
                <p style={{ fontSize: 10, color: T.teal, marginTop: 3 }}>✓ Session tariff: {fmt(tariffs.find(t => t.serviceType === serviceForm.serviceType).negotiatedPrice)} {cur}</p>
              )}
            </Fld>
            {serviceForm.quantity && serviceForm.unitPrice && (
              <div style={{ background: T.tealPale, borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, fontWeight: 700, color: T.teal }}>
                Total: {fmt(parseFloat(serviceForm.quantity || 0) * parseFloat(serviceForm.unitPrice || 0))} {cur}
                <span style={{ fontSize: 10, color: T.muted, fontWeight: 400, marginLeft: 8 }}>({taxLabel(serviceForm.serviceType, property)})</span>
              </div>
            )}
            <Fld label="Notes"><Inp value={serviceForm.notes} onChange={v => setServiceForm(f => ({ ...f, notes: v }))} /></Fld>
            <PrimaryBtn onClick={saveService} loading={saving} full variant="teal">Save Service</PrimaryBtn>
          </Modal>
        )}

        {/* Create reservations — all pending guests */}
        {modal === 'createRes' && (
          <Modal title="Create Reservations for All Pending Guests" onClose={() => { setModal(null); setAvailableRooms([]); }}>
            <div style={{ background: T.tealPale, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 11, color: T.teal }}>
              Creates a tentative corporate reservation for every guest without one, using the session dates ({fmtDate(sess.startDate)} — {fmtDate(sess.endDate)}).
              Folio charges are recorded immediately. Specific rooms can be assigned per guest afterward.
            </div>
            <Fld label="Room Type (optional)" hint="Pre-assign a room category. Individual rooms are assigned per guest.">
              <Sel value={createResForm.roomTypeId} onChange={v => setCreateResForm(f => ({ ...f, roomTypeId: v }))}>
                <option value="">— No room type —</option>
                {roomTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name} ({rt.code})</option>)}
              </Sel>
            </Fld>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <PrimaryBtn onClick={() => handleCreateReservations(null)} loading={saving} full variant="teal">
                🏨 Create {pendingGuests} Reservation(s)
              </PrimaryBtn>
              <button onClick={() => { setModal(null); setAvailableRooms([]); }} style={{ background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 9, padding: '11px 18px', cursor: 'pointer', fontFamily: FONTS.body }}>Cancel</button>
            </div>
          </Modal>
        )}

        {/* Create reservation — single guest (two-step: Room Type → specific Room) */}
        {modal === 'createOneRes' && serviceTarget && (
          <Modal title={`Create Reservation — ${serviceTarget.displayName}`} onClose={() => { setModal(null); setAvailableRooms([]); }}>
            <div style={{ background: T.tealPale, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 11, color: T.teal }}>
              Session dates: <strong>{fmtDate(sess.startDate)}</strong> → <strong>{fmtDate(sess.endDate)}</strong>
            </div>

            {/* Step 1 — Room Type */}
            <Fld label="Step 1 — Room Type" required hint="Choose a category to see available rooms.">
              <Sel value={createResForm.roomTypeId} onChange={v => setCreateResForm(f => ({ ...f, roomTypeId: v, roomId: '' }))}>
                <option value="">— Select room type —</option>
                {roomTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name} ({rt.code})</option>)}
              </Sel>
            </Fld>

            {/* Step 2 — Specific Room */}
            {createResForm.roomTypeId && (
              <Fld
                label="Step 2 — Select Room"
                required
                hint={roomsLoading ? 'Loading rooms…' : `${availableRooms.length} room(s) in this category`}
              >
                {roomsLoading ? (
                  <div style={{ padding: '9px 12px', color: T.muted, fontSize: 12 }}>⏳ Loading…</div>
                ) : availableRooms.length === 0 ? (
                  <div style={{ padding: '9px 12px', background: '#fff8e1', border: '1px solid #f59e0b', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                    ⚠️ No rooms found for this type.
                  </div>
                ) : (
                  <Sel value={createResForm.roomId} onChange={v => setCreateResForm(f => ({ ...f, roomId: v }))}>
                    <option value="">— Select a room —</option>
                    {availableRooms.map(rm => (
                      <option key={rm.id} value={rm.id}>
                        Room {rm.roomNumber}{rm.floor ? ` · Floor ${rm.floor}` : ''}{rm.description ? ` · ${rm.description}` : ''}
                      </option>
                    ))}
                  </Sel>
                )}
              </Fld>
            )}

            {/* Confirmation badge */}
            {createResForm.roomId && (() => {
              const rm = availableRooms.find(r => r.id === createResForm.roomId);
              return rm ? (
                <div style={{ background: T.teal + '15', border: `1px solid ${T.teal}40`, borderRadius: 8, padding: '7px 12px', marginBottom: 10, fontSize: 12, color: T.teal }}>
                  ✓ <strong>Room {rm.roomNumber}</strong> selected{rm.floor ? ` — Floor ${rm.floor}` : ''}
                </div>
              ) : null;
            })()}

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <PrimaryBtn
                onClick={() => handleCreateReservations(serviceTarget.id)}
                loading={saving}
                full
                variant="teal"
                disabled={!createResForm.roomTypeId || (availableRooms.length > 0 && !createResForm.roomId)}
              >
                🏨 Create Reservation
              </PrimaryBtn>
              <button onClick={() => { setModal(null); setAvailableRooms([]); }} style={{ background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 9, padding: '11px 18px', cursor: 'pointer', fontFamily: FONTS.body }}>Cancel</button>
            </div>
          </Modal>
        )}

        {/* Payment request form */}
        {modal === 'request' && (
          <Modal title={editRequest ? 'Edit Payment Request' : 'New Payment Request'} onClose={() => setModal(null)}>
            <div style={{ background: T.tealPale, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 11, color: T.teal }}>
              Session estimated: <strong>{fmt(estimated)} {cur}</strong> · Already invoiced: <strong>{fmt(sess.totalInvoiced || 0)} {cur}</strong>
            </div>
            <Fld label="Amount to Request" required><Inp type="number" value={requestForm.requestedAmount} onChange={v => setRequestForm(f => ({ ...f, requestedAmount: v }))} /></Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Currency"><Inp value={requestForm.currency} onChange={v => setRequestForm(f => ({ ...f, currency: v }))} /></Fld>
              <Fld label="Due Date"><Inp type="date" value={requestForm.dueDate} onChange={v => setRequestForm(f => ({ ...f, dueDate: v }))} /></Fld>
            </div>
            <Fld label="Payment Instructions / Bank Details"><Inp value={requestForm.paymentInstructions} rows={3} onChange={v => setRequestForm(f => ({ ...f, paymentInstructions: v }))} placeholder="Bank name, account number, MoMo code…" /></Fld>
            <Fld label="Notes"><Inp value={requestForm.notes} rows={2} onChange={v => setRequestForm(f => ({ ...f, notes: v }))} /></Fld>
            <PrimaryBtn onClick={saveRequest} loading={saving} full>{editRequest ? 'Update Request' : 'Issue Payment Request'}</PrimaryBtn>
          </Modal>
        )}

        {/* Record installment */}
        {modal === 'install' && installTarget && (
          <Modal title={`Record Payment — ${installTarget.requestNumber}`} onClose={() => setModal(null)}>
            <div style={{ background: T.warningPale, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 11, color: T.warning }}>
              Outstanding: <strong>{fmt(installTarget.requestedAmount - (installTarget.amountPaid || 0))} {installTarget.currency || cur}</strong>
            </div>
            <Fld label="Amount Received" required><Inp type="number" value={installForm.amount} onChange={v => setInstallForm(f => ({ ...f, amount: v }))} /></Fld>
            <Fld label="Payment Method"><Sel value={installForm.paymentMethod} onChange={v => setInstallForm(f => ({ ...f, paymentMethod: v }))}>{PAYMENT_METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}</Sel></Fld>
            <Fld label="Reference / Code"><Inp value={installForm.reference} onChange={v => setInstallForm(f => ({ ...f, reference: v }))} placeholder="Bank ref, MoMo code…" /></Fld>
            <Fld label="Notes"><Inp value={installForm.notes} rows={2} onChange={v => setInstallForm(f => ({ ...f, notes: v }))} /></Fld>
            <PrimaryBtn onClick={saveInstall} loading={saving} full variant="success">✓ Record Payment</PrimaryBtn>
          </Modal>
        )}
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * SESSION LIST VIEW
   * ════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="fade-up">
      {toast && <Toast msg={toast} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.teal, cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: 0 }}>← {account.name}</button>
        <span style={{ color: T.muted }}>›</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Sessions</span>
        <p style={{ fontSize: 12, color: T.muted, marginLeft: 4 }}>({sessions.length})</p>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={() => { setEditSession(null); setSessionForm(emptySession()); setModal('session'); }}
            style={{ background: T.navy, border: 'none', borderRadius: 9, padding: '9px 20px', fontSize: 13, fontWeight: 700, color: T.white, cursor: 'pointer', fontFamily: FONTS.body }}>
            + New Session
          </button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 60, color: T.muted, fontSize: 12 }}>Loading sessions…</div>}

      {!loading && sessions.length === 0 && (
        <div style={{ background: T.surface, border: `2px dashed ${T.border}`, borderRadius: 14, padding: 40, textAlign: 'center' }}>
          <p style={{ fontSize: 32, marginBottom: 12 }}>📁</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: T.navy, marginBottom: 6 }}>{account.name} has no sessions yet</p>
          <p style={{ fontSize: 12, color: T.muted, marginBottom: 20 }}>Create a session for each event or booking period. Each session has its own guests, tariffs, and payment requests.</p>
          <button onClick={() => { setEditSession(null); setSessionForm(emptySession()); setModal('session'); }}
            style={{ background: T.teal, border: 'none', borderRadius: 9, padding: '10px 24px', fontSize: 13, fontWeight: 700, color: T.white, cursor: 'pointer', fontFamily: FONTS.body }}>
            + Create First Session
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 14 }}>
        {sessions.map(s => {
          const c         = s.currency || currency;
          const est       = Number(s.totalEstimated || 0);
          const pd        = Number(s.totalPaid || 0);
          const p         = est > 0 ? Math.min(100, Math.round((pd / est) * 100)) : 0;
          const sc        = SESSION_STATUS[s.status] || { color: T.muted, bg: T.surface, label: s.status };
          return (
            <div key={s.id} onClick={() => openSession(s)}
              style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', cursor: 'pointer', boxShadow: SHADOW.sm, transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = SHADOW.md; e.currentTarget.style.borderColor = T.teal + '60'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW.sm; e.currentTarget.style.borderColor = T.border; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.navy }}>{s.sessionName}</div>
                <SBadge status={s.status} />
              </div>
              <div style={{ fontSize: 11, color: T.muted, marginBottom: 14 }}>📅 {fmtDate(s.startDate)} — {fmtDate(s.endDate)}</div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 10, color: T.muted }}>
                  <span>Payment Progress</span><span>{p}%</span>
                </div>
                <div style={{ height: 5, background: T.border, borderRadius: 99 }}>
                  <div style={{ height: 5, width: `${p}%`, background: p >= 100 ? T.success : T.teal, borderRadius: 99 }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 10, color: T.muted }}>Estimated<br /><strong style={{ fontSize: 12, color: T.navy }}>{fmt(est)} {c}</strong></div>
                <div style={{ fontSize: 10, color: T.muted, textAlign: 'right' }}>Paid<br /><strong style={{ fontSize: 12, color: T.success }}>{fmt(pd)} {c}</strong></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* New session modal (shown from list view) */}
      {modal === 'session' && (
        <Modal title={`New Session — ${account.name}`} onClose={() => setModal(null)} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Fld label="Session Name" required><Inp value={sessionForm.sessionName} onChange={v => setSessionForm(f => ({ ...f, sessionName: v }))} placeholder="July Leadership Training" /></Fld>
            <Fld label="Status"><Sel value={sessionForm.status} onChange={v => setSessionForm(f => ({ ...f, status: v }))}>{Object.entries(SESSION_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</Sel></Fld>
          </div>
          <Fld label="Description"><Inp value={sessionForm.description} rows={2} onChange={v => setSessionForm(f => ({ ...f, description: v }))} /></Fld>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Fld label="Start Date" required><Inp type="date" value={sessionForm.startDate} onChange={v => setSessionForm(f => ({ ...f, startDate: v }))} /></Fld>
            <Fld label="End Date" required><Inp type="date" value={sessionForm.endDate} onChange={v => setSessionForm(f => ({ ...f, endDate: v }))} /></Fld>
            <Fld label="Currency"><Inp value={sessionForm.currency} onChange={v => setSessionForm(f => ({ ...f, currency: v }))} placeholder="RWF" /></Fld>
          </div>
          <Fld label="Total Conference Participants (optional)"
               hint="Everyone attending — including the people you register individually later (they are part of this total, not extra). Only people who need rooms or personal services must be registered.">
            <Inp type="number" value={sessionForm.participantCount}
                 onChange={v => setSessionForm(f => ({ ...f, participantCount: v }))}
                 placeholder="e.g. 80" />
          </Fld>
          <Fld label="Terms & Conditions" hint="Printed on all proforma invoices and payment requests for this session."><Inp value={sessionForm.termsAndConditions} rows={4} onChange={v => setSessionForm(f => ({ ...f, termsAndConditions: v }))} /></Fld>
          <Fld label="Notes"><Inp value={sessionForm.notes} rows={2} onChange={v => setSessionForm(f => ({ ...f, notes: v }))} /></Fld>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <PrimaryBtn onClick={saveSession} loading={saving} full>Create Session</PrimaryBtn>
            <button onClick={() => setModal(null)} style={{ background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 9, padding: '11px 18px', cursor: 'pointer', fontFamily: FONTS.body }}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}